<template>
  <div class="live-detail">
    <div class="toast-container" v-if="toast.show">
      <div class="toast-message" :class="toast.type">
        <svg v-if="toast.type === 'success'" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
        <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
        {{ toast.message }}
      </div>
    </div>

    <div class="container">
      <div class="live-content">
        <!-- 左侧直播区域 -->
        <div class="live-main">
          <div class="video-container" ref="videoContainer" :style="videoContainerStyle">
            <video 
              v-if="!videoError"
              class="live-video"
              autoplay
              loop
              muted
              :src="videoUrl"
              @error="handleVideoError"
              @loadedmetadata="handleVideoMetadata"
              @timeupdate="handleTimeUpdate"
              ref="videoPlayer"
            >
              您的浏览器不支持视频播放
            </video>
            <img v-else class="live-video-fallback" :src="videoPoster" alt="直播封面">

            <!-- 顶部直播状态条 -->
            <div class="live-topbar">
              <span class="live-badge">
                <i class="live-dot"></i>直播中
              </span>
              <span class="live-stat">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/></svg>
                {{ formatNumber(onlineCount) }} 在线
              </span>
              <span class="live-stat heat-stat">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/><path d="M12 6v6l4 2"/></svg>
                热度 {{ formatNumber(heat) }}
              </span>
              <span class="live-stat danmaku-count">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
                {{ formatNumber(danmakuList.length) }}
              </span>
            </div>

            <!-- 弹幕层 -->
            <DanmakuLayer
              ref="danmakuLayer"
              :list="danmakuList"
              :current-time="videoCurrentTime"
              :visible="danmakuSetting.enable"
              :opacity="danmakuSetting.opacity"
              :duration="danmakuSetting.duration"
              :scale="danmakuSetting.scale"
              :track-count="danmakuSetting.tracks"
            />

            <!-- 礼物飘屏 -->
            <div class="gift-banner-area">
              <transition-group name="gift-banner">
                <div v-for="b in giftBanners" :key="b.key" class="gift-banner">
                  <img v-if="b.cover" :src="b.cover" class="gift-banner-img" alt="">
                  <span class="gift-banner-user">{{ b.nickname }}</span>
                  <span class="gift-banner-text">送出 {{ b.giftName }}</span>
                  <span class="gift-banner-count">x{{ b.count }}</span>
                </div>
              </transition-group>
            </div>

            <!-- 双击点赞飘心 -->
            <div class="heart-area" @dblclick="handleDoubleLike">
              <div v-for="h in hearts" :key="h.key" class="heart" :style="h.style">❤</div>
            </div>

            <!-- 视频底部控制栏 -->
            <div class="video-controls">
              <div class="controls-right">
                <button class="control-btn dm-toggle" :class="{ off: !danmakuSetting.enable }" @click="toggleDanmaku" :title="danmakuSetting.enable ? '关闭弹幕' : '开启弹幕'">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
                </button>
                <button class="control-btn" @click="showDanmakuSetting = !showDanmakuSetting" title="弹幕设置">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>
                </button>
                <!-- 画质选择 -->
                <div class="quality-wrap" @click.stop>
                  <button class="control-btn quality-btn" @click.stop="toggleQualityPanel" title="画质">
                    <span class="quality-text">{{ qualityLabel }}</span>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/></svg>
                  </button>
                  <div class="quality-panel" v-if="showQualityPanel" @click.stop>
                    <button class="quality-option" :class="{ active: videoQuality === '480' }" @click="selectQuality('480')">
                      <span class="quality-opt-label">480P</span>
                      <span class="quality-opt-desc">流畅</span>
                    </button>
                    <button class="quality-option" :class="{ active: videoQuality === '720' }" @click="selectQuality('720')">
                      <span class="quality-opt-label">720P</span>
                      <span class="quality-opt-desc">高清</span>
                    </button>
                    <button class="quality-option" :class="{ active: videoQuality === '1080' }" @click="selectQuality('1080')">
                      <span class="quality-opt-label">1080P</span>
                      <span class="quality-opt-desc">超清</span>
                      <span class="quality-lock" v-if="videoQuality !== '1080'">
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </span>
                    </button>
                  </div>
                </div>
                <button class="control-btn" @click="toggleMute" :title="volume > 0 ? '静音' : '开启声音'">
                  <svg v-if="volume > 0" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>
                  <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
                </button>
                <div class="volume-slider">
                  <input type="range" min="0" max="100" v-model="volume" @input="handleVolume">
                </div>
                <button class="control-btn" @click="toggleFullscreen" title="全屏">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>
                </button>
              </div>
            </div>

            <!-- 弹幕设置面板 -->
            <div class="dm-setting" v-if="showDanmakuSetting" @click.stop>
              <div class="dm-setting-row">
                <span class="dm-setting-label">不透明度</span>
                <input type="range" min="20" max="100" v-model.number="opacityPercent">
                <span class="dm-setting-value">{{ opacityPercent }}%</span>
              </div>
              <div class="dm-setting-row">
                <span class="dm-setting-label">弹幕速度</span>
                <input type="range" min="5" max="20" v-model.number="danmakuSetting.duration">
                <span class="dm-setting-value">{{ danmakuSetting.duration }}s</span>
              </div>
              <div class="dm-setting-row">
                <span class="dm-setting-label">字号</span>
                <button v-for="s in fontScales" :key="s.value" class="dm-chip" :class="{ active: danmakuSetting.scale === s.value }" @click="danmakuSetting.scale = s.value">{{ s.label }}</button>
              </div>
              <div class="dm-setting-row">
                <span class="dm-setting-label">显示区域</span>
                <button v-for="t in trackOptions" :key="t.value" class="dm-chip" :class="{ active: danmakuSetting.tracks === t.value }" @click="danmakuSetting.tracks = t.value">{{ t.label }}</button>
              </div>
            </div>

            <!-- 礼物特效层 -->
            <canvas class="gift-effect-container" ref="giftEffectContainer" id="gift-effect-canvas"></canvas>
            <img v-if="pngEffect.show" :src="pngEffect.url" class="gift-effect-png" :style="{ animationDuration: pngEffect.duration + 'ms' }" alt="gift-effect" />

            <!-- 进场特效层（简洁精致型） -->
            <div v-if="enterEffect.show" class="enter-effect" :class="{ 'is-noble': enterEffect.noble_level > 0 }" :key="enterEffect.key">
              <!-- 柔和光晕 -->
              <div class="enter-aura"></div>
              <!-- 横幅主体 -->
              <div class="enter-banner">
                <!-- 头像（贵族戴皇冠） -->
                <div class="enter-banner-avatar">
                  <svg v-if="enterEffect.noble_level > 0" class="enter-crown" viewBox="0 0 64 40" aria-hidden="true">
                    <path d="M4 36 L0 12 L18 22 L32 4 L46 22 L64 12 L60 36 Z" fill="currentColor"/>
                    <circle cx="32" cy="7" r="4.5" fill="currentColor"/>
                  </svg>
                  <div class="enter-avatar-ring"></div>
                  <img v-if="enterEffect.avatar" :src="fullUrl(enterEffect.avatar)" class="enter-banner-avatar-img" @error="handleEnterAvatarError" alt="">
                  <svg v-else class="enter-banner-avatar-img" viewBox="0 0 80 80"><circle cx="40" cy="40" r="36" fill="#ff9600"/></svg>
                </div>
                <!-- 文字 -->
                <div class="enter-banner-text">
                  <span class="enter-banner-welcome">欢迎</span>
                  <span class="enter-banner-name">{{ enterEffect.nickname || '神秘用户' }}</span>
                  <span class="enter-banner-come">进入直播间</span>
                  <img v-if="enterEffect.noble_level > 0 && getNobleIcon(enterEffect.noble_level)" :src="getNobleIcon(enterEffect.noble_level)" class="enter-banner-noble-icon" :title="'贵族Lv' + enterEffect.noble_level" alt="" @error="e => e.target.style.display = 'none'">
                  <span v-else-if="enterEffect.noble_level > 0" class="enter-banner-noble">贵{{ enterEffect.noble_level }}</span>
                  <span class="enter-fan-badge" v-if="enterEffect.fan_level > 0">
                    <span class="enter-fan-name">{{ enterEffect.fan_badge_name }}</span>
                    <img v-if="enterEffect.fan_level >= 1 && enterEffect.fan_level <= 20" :src="`/fan_badge/fan_${enterEffect.fan_level}.png`" class="enter-fan-img" :title="'粉丝' + enterEffect.fan_level + '级'" alt="" @error="e => e.target.style.display = 'none'">
                    <img v-else-if="enterEffect.fan_level >= 21 && enterEffect.fan_level <= 50" :src="`/fan_badge/fan_${enterEffect.fan_level}.webp`" class="enter-fan-img" :title="'粉丝' + enterEffect.fan_level + '级'" alt="" @error="e => e.target.style.display = 'none'">
                    <span v-else class="enter-fan-lv">{{ enterEffect.fan_level }}</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- 排行榜/红包/抽奖/连麦面板通过工具栏"更多"按钮触发，避免遮挡视频 -->

            <!-- 排行榜面板 -->
            <div v-if="showRankPanel" class="interact-panel rank-panel" @click.stop>
              <div class="panel-header-sm">
                <span>贡献榜</span>
                <button class="panel-close" @click="showRankPanel = false">×</button>
              </div>
              <div class="rank-period-tabs">
                <span :class="{active: rankPeriod === 'day'}" @click="rankPeriod = 'day'; fetchRankList()">日榜</span>
                <span :class="{active: rankPeriod === 'week'}" @click="rankPeriod = 'week'; fetchRankList()">周榜</span>
                <span :class="{active: rankPeriod === 'month'}" @click="rankPeriod = 'month'; fetchRankList()">月榜</span>
                <span :class="{active: rankPeriod === 'all'}" @click="rankPeriod = 'all'; fetchRankList()">总榜</span>
              </div>
              <div class="rank-list">
                <div class="rank-item" v-for="(r, i) in rankList" :key="r.user_id" :class="{top: i < 3}">
                  <span class="rank-num" :class="'rank-' + (i + 1)">{{ i + 1 }}</span>
                  <img :src="r.avatar || defaultAvatar" class="rank-avatar" @error="handleImgError">
                  <span class="rank-name">{{ r.nickname }}</span>
                  <span class="rank-amount">¥{{ r.amount || r.count || 0 }}</span>
                </div>
                <div v-if="rankList.length === 0" class="empty-text-sm">暂无数据</div>
              </div>
            </div>

            <!-- 红包面板 -->
            <div v-if="showRedPacketPanel" class="interact-panel redpacket-panel" @click.stop>
              <div class="panel-header-sm">
                <span>直播间红包</span>
                <button class="panel-close" @click="showRedPacketPanel = false">×</button>
              </div>
              <div class="redpacket-actions">
                <button class="send-packet-btn" @click="showSendPacketForm = !showSendPacketForm">发红包</button>
              </div>
              <div v-if="showSendPacketForm" class="send-packet-form">
                <input type="number" v-model="packetForm.totalAmount" placeholder="总金额" min="0.01">
                <input type="number" v-model="packetForm.count" placeholder="红包个数" min="1">
                <select v-model="packetForm.type">
                  <option value="random">拼手气</option>
                  <option value="fixed">平均分</option>
                </select>
                <input type="text" v-model="packetForm.message" placeholder="祝福语（选填）" maxlength="20">
                <button class="btn-send-packet" @click="handleSendPacket">发送</button>
              </div>
              <div class="packet-list">
                <div class="packet-item" v-for="p in packetList" :key="p.id" :class="{grabbed: p.my_grabbed, empty: p.remain_count === 0}">
                  <div class="packet-info">
                    <div class="packet-sender">{{ p.sender_name }}</div>
                    <div class="packet-detail">{{ p.message || '恭喜发财' }} · {{ p.remain_count }}/{{ p.count }}个</div>
                  </div>
                  <button v-if="p.remain_count > 0 && !p.my_grabbed" class="grab-btn" @click="handleGrabPacket(p.id)">抢</button>
                  <span v-else-if="p.my_grabbed" class="grabbed-text">已抢 ¥{{ p.my_amount || 0 }}</span>
                  <span v-else class="empty-text">已抢完</span>
                </div>
                <div v-if="packetList.length === 0" class="empty-text-sm">暂无红包</div>
              </div>
            </div>

            <!-- 抽奖面板 -->
            <div v-if="showLotteryPanel" class="interact-panel lottery-panel" @click.stop>
              <div class="panel-header-sm">
                <span>直播间抽奖</span>
                <button class="panel-close" @click="showLotteryPanel = false">×</button>
              </div>
              <div v-if="isAnchor" class="anchor-lottery-actions">
                <button class="create-lottery-btn" @click="showCreateLotteryForm = !showCreateLotteryForm">发起抽奖</button>
              </div>
              <div v-if="showCreateLotteryForm && isAnchor" class="create-lottery-form">
                <input type="text" v-model="lotteryForm.title" placeholder="抽奖标题" maxlength="20">
                <input type="text" v-model="lotteryForm.prize" placeholder="奖品名称" maxlength="30">
                <input type="number" v-model="lotteryForm.prizeCount" placeholder="奖品数量" min="1">
                <input type="number" v-model="lotteryForm.duration" placeholder="持续秒数" min="60">
                <select v-model="lotteryForm.condition">
                  <option value="all">所有人</option>
                  <option value="follow">仅粉丝</option>
                  <option value="guard">仅守护</option>
                </select>
                <button class="btn-create-lottery" @click="handleCreateLottery">开始抽奖</button>
              </div>
              <div v-if="currentLottery" class="current-lottery">
                <div class="lottery-title">{{ currentLottery.title }}</div>
                <div class="lottery-prize">奖品：{{ currentLottery.prize }} × {{ currentLottery.prize_count }}</div>
                <div class="lottery-meta">{{ currentLottery.participant_count || 0 }}人参与 · {{ currentLottery.status === 0 ? '进行中' : '已开奖' }}</div>
                <div v-if="currentLottery.status === 0 && !isAnchor" class="lottery-actions">
                  <button class="join-lottery-btn" @click="handleJoinLottery" :disabled="currentLottery.my_joined">
                    {{ currentLottery.my_joined ? '已参与' : '参与抽奖' }}
                  </button>
                </div>
                <div v-if="currentLottery.status === 0 && isAnchor" class="lottery-actions">
                  <button class="draw-lottery-btn" @click="handleDrawLottery">立即开奖</button>
                </div>
                <div v-if="currentLottery.winners && currentLottery.winners.length > 0" class="lottery-winners">
                  <div class="winners-title">中奖名单</div>
                  <div class="winner-item" v-for="w in currentLottery.winners" :key="w.user_id">
                    {{ w.nickname }}
                  </div>
                </div>
              </div>
              <div v-else class="empty-text-sm">暂无进行中的抽奖</div>
            </div>

            <!-- 连麦/PK面板 -->
            <div v-if="showPkPanel && isAnchor" class="interact-panel pk-panel" @click.stop>
              <div class="panel-header-sm">
                <span>连麦 / PK</span>
                <button class="panel-close" @click="showPkPanel = false">×</button>
              </div>
              <div class="pk-tabs">
                <span :class="{active: pkTab === 'pk'}" @click="pkTab = 'pk'">PK对战</span>
                <span :class="{active: pkTab === 'mic'}" @click="pkTab = 'mic'">连麦</span>
              </div>
              <div v-if="pkTab === 'pk'">
                <div v-if="!currentPk" class="pk-create">
                  <input type="text" v-model="pkForm.targetRoomId" placeholder="对方房间号">
                  <input type="number" v-model="pkForm.duration" placeholder="PK时长(秒)" min="60">
                  <button class="btn-start-pk" @click="handleStartPk">发起PK</button>
                </div>
                <div v-else class="pk-info">
                  <div class="pk-progress">
                    <div class="pk-side a" :style="{width: pkProgressA + '%'}">
                      <span>{{ currentPk.anchor_a_name }}</span>
                      <span>{{ currentPk.score_a || 0 }}</span>
                    </div>
                    <div class="pk-side b" :style="{width: (100 - pkProgressA) + '%'}">
                      <span>{{ currentPk.score_b || 0 }}</span>
                      <span>{{ currentPk.anchor_b_name }}</span>
                    </div>
                  </div>
                  <div class="pk-timer" v-if="currentPk.status === 1">剩余：{{ pkRemaining }}</div>
                  <button v-if="currentPk.status === 0 && currentPk.anchor_b === userId" class="btn-accept-pk" @click="handleAcceptPk">接受PK</button>
                  <button v-if="currentPk.status === 0 && currentPk.anchor_b === userId" class="btn-reject-pk" @click="handleRejectPk">拒绝</button>
                  <button v-if="currentPk.status === 1" class="btn-end-pk" @click="handleEndPk">结束PK</button>
                </div>
              </div>
              <div v-if="pkTab === 'mic'">
                <div class="mic-create">
                  <input type="number" v-model="micForm.targetUserId" placeholder="对方用户ID">
                  <button class="btn-start-mic" @click="handleStartMic">邀请连麦</button>
                </div>
                <div class="mic-list">
                  <div class="mic-item" v-for="m in micList" :key="m.id">
                    <span>{{ m.guest_name }}</span>
                    <span class="mic-status">{{ m.status === 0 ? '等待中' : m.status === 1 ? '连麦中' : '已结束' }}</span>
                    <button v-if="m.status === 1" class="btn-end-mic" @click="handleEndMic(m.id)">结束</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 虎牙风格底部工具栏 -->
          <div class="live-toolbar">
            <!-- 左侧快捷入口 -->
            <div class="toolbar-group toolbar-left">
              <button class="toolbar-btn tool-icon" @click="openToolbarModal('treasure')" title="百宝箱">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/><path d="M12 6c-2.76 0-5 2.24-5 5 0 2.88 2.48 4.64 5 7.64 2.52-3 5-4.76 5-7.64 0-2.76-2.24-5-5-5zm0 7c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>
                <span>百宝箱</span>
              </button>
              <button class="toolbar-btn tool-icon" v-if="!minorMode" @click="handleHalfPriceDraw" title="抽半价礼物">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                <span>抽半价</span>
              </button>
              <button class="toolbar-btn tool-icon" @click="openToolbarPanel('more')" title="更多">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>
                <span>更多</span>
              </button>
              <button class="dm-like-btn" @click="handleLike" title="点赞">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                {{ formatNumber(likeCount) }}
              </button>
            </div>

            <!-- 中间礼物快捷区 -->
            <div class="toolbar-group toolbar-center" v-if="!minorMode">
              <div class="quick-gifts">
                <button v-for="gift in quickGifts" :key="gift.id" class="quick-gift" @click="sendQuickGift(gift)" :title="gift.name + ' ' + gift.price + '金币'">
                  <img :src="gift.displayImage" alt="">
                </button>
                <button class="quick-gift expand-gift" @click="openToolbarModal('gift')" title="展开礼物面板">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/></svg>
                </button>
              </div>
            </div>

            <!-- 右侧功能入口 -->
            <div class="toolbar-group toolbar-right">
              <button class="toolbar-btn action-btn recharge" v-if="!minorMode" @click="openToolbarModal('recharge')" title="充值">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>
                <span>充值</span>
              </button>
              <button class="toolbar-btn action-btn" @click="openToolbarModal('bag')" title="包裹">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 6h-2c0-2.76-2.24-5-5-5S7 3.24 7 6H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-7-3c1.66 0 3 1.34 3 3H9c0-1.66 1.34-3 3-3zm7 17H5V8h14v12z"/></svg>
                <span>包裹</span>
              </button>
              <button class="toolbar-btn action-btn" v-if="!minorMode" @click="openGuardModal" title="守护">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>
                <span>守护</span>
              </button>
              <button class="toolbar-btn action-btn noble" v-if="!minorMode" @click="openNobleModal" title="贵族">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/></svg>
                <span>贵族</span>
              </button>
            </div>
          </div>

          <!-- 工具栏扩展面板 -->
          <div v-if="toolbarPanel" class="toolbar-panel" @click.stop>
            <div class="panel-header-sm">
              <span>{{ toolbarPanelTitle }}</span>
              <button class="panel-close" @click="closeToolbarPanel">×</button>
            </div>
            <div class="panel-body">
              <!-- 更多菜单 -->
              <div v-if="toolbarPanel === 'more'" class="more-menu">
                <button class="more-item" @click="openInteractPanel('rank'); closeToolbarPanel()">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M7.5 21H2V9h5.5v12zm7.25-18h-5.5v18h5.5V3zM22 11h-5.5v10H22V11z"/></svg>
                  <span>排行榜</span>
                </button>
                <button class="more-item" v-if="!minorMode" @click="openInteractPanel('redpacket'); closeToolbarPanel()">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 00-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z"/></svg>
                  <span>红包</span>
                </button>
                <button class="more-item" v-if="!minorMode" @click="openInteractPanel('lottery'); closeToolbarPanel()">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                  <span>抽奖</span>
                </button>
                <button v-if="isAnchor" class="more-item" @click="openInteractPanel('pk'); closeToolbarPanel()">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M14.5 17.5L3 6V3h3l11.5 11.5M13 19l6-6m-3 3l4 4m-2-2l2-2"/></svg>
                  <span>连麦/PK</span>
                </button>
              </div>
            </div>
          </div>

          <!-- 虎牙风格开通弹窗：贵族 -->
          <transition name="modal-fade">
            <div v-if="showNobleModal && !minorMode" class="lhy-modal-mask" @click.self="closeNobleModal">
              <div class="lhy-modal">
                <div class="lhy-modal-head">
                  <div class="lhy-modal-title">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/></svg>
                    开通贵族
                  </div>
                  <button class="lhy-modal-close" @click="closeNobleModal">×</button>
                </div>
                <div class="lhy-modal-body">
                  <div v-if="myNoble" class="lhy-current">
                    <span class="lhy-current-label">当前贵族</span>
                    <span class="lhy-current-name">Lv{{ myNoble.level }} {{ myNoble.name || '' }}</span>
                  </div>
                  <div v-else class="lhy-current none">尚未开通贵族</div>
                  <div class="lhy-level-list">
                    <div
                      v-for="n in nobleLevels"
                      :key="n.id"
                      class="lhy-level-item"
                      :class="{ active: myNoble?.level === n.level }"
                      @click="buyNobleLevel(n)"
                    >
                      <img :src="getFullImageUrl(n.icon) || defaultAvatar" alt="">
                      <div class="lhy-level-info">
                        <span class="lhy-level-name">{{ n.name }}</span>
                        <span class="lhy-level-benefit">{{ n.benefits?.badge || '专属贵族标识' }}</span>
                      </div>
                      <div class="lhy-level-buy">
                        <span class="lhy-level-price">{{ n.price }}金币/月</span>
                        <button class="lhy-buy-btn" :class="{ owned: myNoble?.level === n.level }" @click.stop="buyNobleLevel(n)">
                          {{ myNoble?.level === n.level ? '已开通' : '立即开通' }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-if="!nobleLevels.length" class="empty-text-sm">暂无贵族等级</div>
                </div>
              </div>
            </div>
          </transition>

          <!-- 虎牙风格开通弹窗：守护 -->
          <transition name="modal-fade">
            <div v-if="showGuardModal && !minorMode" class="lhy-modal-mask" @click.self="closeGuardModal">
              <div class="lhy-modal">
                <div class="lhy-modal-head">
                  <div class="lhy-modal-title">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>
                    开通守护
                  </div>
                  <button class="lhy-modal-close" @click="closeGuardModal">×</button>
                </div>
                <div class="lhy-modal-body">
                  <div class="lhy-guard-anchor" v-if="videoDetail?.user">
                    <img :src="authorAvatar" @error="handleAvatarError" alt="">
                    <div>
                      <span class="lhy-guard-anchor-name">{{ videoDetail.user.nickname || '未知主播' }}</span>
                      <span class="lhy-guard-anchor-tip">守护{{ videoDetail.user.nickname || '该主播' }}，获得专属特权</span>
                    </div>
                  </div>
                  <div v-if="myGuard" class="lhy-current">
                    <span class="lhy-current-label">当前守护</span>
                    <span class="lhy-current-name">Lv{{ myGuard.level }} {{ myGuard.name || '' }}</span>
                    <span class="lhy-current-expire" v-if="myGuard.days_left >= 0">（剩{{ myGuard.days_left }}天）</span>
                    <span class="lhy-current-expire" v-else>（永久）</span>
                  </div>
                  <div class="lhy-level-list">
                    <div
                      v-for="g in guardList"
                      :key="g.level || g.id"
                      class="lhy-level-item"
                      :class="{ active: myGuard?.level === g.level }"
                      @click="buyGuardLevel(g)"
                    >
                      <img :src="getFullImageUrl(g.icon) || getGuardIcon(g.level)" alt="">
                      <div class="lhy-level-info">
                        <span class="lhy-level-name">{{ g.name }}</span>
                        <span class="lhy-level-benefit">{{ (g.privileges || []).slice(0, 2).join(' · ') || '专属特权' }}</span>
                      </div>
                      <div class="lhy-level-buy">
                        <span class="lhy-level-price">{{ g.price_num || g.price }}金币/月</span>
                        <button class="lhy-buy-btn" :class="{ owned: myGuard?.level === g.level }" @click.stop="buyGuardLevel(g)">
                          {{ myGuard?.level === g.level ? '已开通' : '开通' }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-if="!guardList.length" class="empty-text-sm">暂无守护等级</div>

                  <div class="lhy-fanclub">
                    <div class="lhy-fanclub-title">粉丝团</div>
                    <p class="lhy-fanclub-desc">加入主播粉丝团，获得专属粉丝徽章、进场特效与互动特权。首次加入需消耗 10 金币。</p>
                    <button class="lhy-fanclub-btn" @click="handleJoinFanClub">
                      {{ fanClubJoined ? '已加入粉丝团' : '加入粉丝团' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </transition>

          <!-- 虎牙风格功能弹窗：礼物/充值/包裹/百宝箱/小店 -->
          <transition name="modal-fade">
            <div v-if="showToolbarModal" class="lhy-modal-mask" @click.self="closeToolbarModal">
              <div class="lhy-modal">
                <div class="lhy-modal-head">
                  <div class="lhy-modal-title">{{ toolbarModalTitle }}</div>
                  <button class="lhy-modal-close" @click="closeToolbarModal">×</button>
                </div>
                <div class="lhy-modal-body">
                  <!-- 选择礼物 -->
                  <template v-if="showToolbarModal === 'gift' && !minorMode">
                    <div class="gift-picker-modal">
                      <div v-for="gift in giftList" :key="gift.id" class="gift-picker-item" :class="{ selected: selectedGift?.id === gift.id }" @click="pickAndSendGift(gift)">
                        <img :src="gift.displayImage" alt="">
                        <span class="gift-picker-name">{{ gift.name }}</span>
                        <span class="gift-picker-price">{{ gift.price }}金币</span>
                      </div>
                      <div v-if="!giftList.length" class="empty-text-sm">暂无礼物</div>
                    </div>
                  </template>

                  <!-- 充值 -->
                  <template v-else-if="showToolbarModal === 'recharge' && !minorMode">
                    <div class="recharge-list">
                      <!-- 注意：接口字段是 coins / bonus_coins / total_coins，
                           原模板写的 pkg.coin 不存在，导致金币数渲染为空，
                           卡片只剩"金币 ¥328.00"，用户看不到 1:10 的比例 -->
                      <div v-for="pkg in rechargePackagesList" :key="pkg.id" class="recharge-item" @click="createRechargeOrder(pkg)">
                        <div class="recharge-coin">{{ pkgBaseCoins(pkg) }}</div>
                        <div class="recharge-unit">金币</div>
                        <div class="recharge-bonus" v-if="Number(pkg.bonus_coins) > 0">+{{ pkg.bonus_coins }} 赠送</div>
                        <div class="recharge-price">¥{{ pkg.price }}</div>
                      </div>
                    </div>
                    <div class="recharge-rate-tip">充值比例 1 元 = 10 金币</div>
                    <div v-if="!rechargePackagesList.length" class="empty-text-sm">暂无充值套餐</div>
                  </template>

                  <!-- 包裹 -->
                  <template v-else-if="showToolbarModal === 'bag'">
                    <div class="bag-panel">
                      <div v-for="item in bagItems" :key="item.id" class="bag-item">
                        <img :src="item.image || item.icon || item.gift?.displayImage || defaultAvatar" alt="">
                        <div class="bag-info">
                          <span class="bag-name">{{ item.name || item.gift?.name || '未知物品' }}</span>
                          <span class="bag-count">x{{ item.quantity || item.count || 1 }}</span>
                        </div>
                        <button class="bag-use" @click="useBagItem(item)" :disabled="item.using">使用</button>
                      </div>
                      <div v-if="!bagItems.length" class="empty-text-sm">背包空空如也</div>
                    </div>
                  </template>

                  <!-- 百宝箱 -->
                  <template v-else-if="showToolbarModal === 'treasure'">
                    <div class="treasure-panel">
                      <div v-if="treasureStatus" class="treasure-status">
                        <div class="treasure-title">我的宝箱</div>
                        <div class="treasure-progress">开启进度：{{ treasureStatus.opened_count || 0 }}/{{ treasureStatus.total_count || 0 }}</div>
                      </div>
                      <div class="treasure-list">
                        <div v-for="t in myTreasures" :key="t.id" class="treasure-item">
                          <img :src="t.image || defaultAvatar" alt="">
                          <span class="treasure-name">{{ t.name }}</span>
                          <span class="treasure-count">x{{ t.count || 1 }}</span>
                        </div>
                      </div>
                      <button class="open-treasure-btn" @click="openTreasureBox" :disabled="openingTreasure">{{ openingTreasure ? '开启中...' : '开启宝箱' }}</button>
                      <div v-if="!myTreasures.length && !treasureStatus" class="empty-text-sm">暂无宝箱数据</div>
                    </div>
                  </template>

                  <!-- 小店 -->
                  <template v-else-if="showToolbarModal === 'shop'">
                    <div class="shop-tabs">
                      <span :class="{active: shopTab === 'items'}" @click="shopTab = 'items'">商品</span>
                      <span :class="{active: shopTab === 'my'}" @click="shopTab = 'my'">我的</span>
                    </div>
                    <div v-if="shopTab === 'items'" class="shop-list">
                      <div v-for="item in shopItems" :key="item.id" class="shop-item">
                        <img :src="item.image || item.cover || defaultAvatar" alt="">
                        <div class="shop-info">
                          <span class="shop-name">{{ item.name }}</span>
                          <span class="shop-price">{{ item.price }}金币</span>
                        </div>
                        <button class="shop-buy" @click="buyShopItem(item)" :disabled="item.buying">购买</button>
                      </div>
                      <div v-if="!shopItems.length" class="empty-text-sm">暂无商品</div>
                    </div>
                    <div v-else class="bag-list">
                      <div v-for="item in bagItems" :key="item.id" class="bag-item">
                        <img :src="item.image || item.icon || item.gift?.displayImage || defaultAvatar" alt="">
                        <div class="bag-info">
                          <span class="bag-name">{{ item.name || item.gift?.name || '未知物品' }}</span>
                          <span class="bag-count">x{{ item.quantity || item.count || 1 }}</span>
                        </div>
                        <button class="bag-use" @click="useBagItem(item)" :disabled="item.using">使用</button>
                      </div>
                      <div v-if="!bagItems.length" class="empty-text-sm">暂无已购商品</div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </transition>

          <div class="video-info">
            <div class="video-title-row">
              <h2 class="video-title">{{ videoDetail?.title || '加载中...' }}</h2>
              <div class="video-title-actions">
                <button class="follow-btn" :class="{ followed: isFollowed }" @click="toggleFollow">{{ isFollowed ? '已关注' : '+ 关注' }}</button>
                <span class="video-view-count">{{ formatNumber(videoDetail?.views || 0) }}</span>
              </div>
            </div>
            <div class="author-info" v-if="videoDetail?.user">
              <div class="author-avatar" @click="goAnchorHome">
                <img :src="authorAvatar" @error="handleAvatarError" alt="主播头像">
              </div>
              <div class="author-details">
                <span class="author-name" @click="goAnchorHome">{{ videoDetail.user.nickname || '未知用户' }}</span>
                <span class="author-id">ID: {{ videoDetail.user_id }}</span>
              </div>
              <div class="author-meta">
                <span class="meta-item category" v-if="videoDetail?.category?.name">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
                  {{ videoDetail.category.name }}
                </span>
                <span class="meta-item rank-tag">全站小时榜</span>
                <span class="meta-item guard-tag">守护榜</span>
              </div>
              <button class="report-link" @click="openReport">举报</button>
            </div>
          </div>

        </div>

        <!-- 充值收款二维码弹窗 -->
        <transition name="modal-fade">
          <div v-if="rechargeQR.show" class="lhy-modal-mask" @click.self="rechargeQR.show = false">
            <div class="recharge-qr-modal">
              <div class="recharge-qr-head">
                <span class="recharge-qr-title">请扫码支付</span>
                <button class="recharge-qr-close" @click="rechargeQR.show = false">×</button>
              </div>
              <div class="recharge-qr-body">
                <div class="recharge-qr-amount">￥{{ rechargeQR.amount }}</div>
                <div class="recharge-qr-desc">请使用支付宝扫码支付</div>
                <div class="recharge-qr-wrap">
                  <img :src="rechargeQR.url" class="recharge-qr-img" alt="收款二维码">
                </div>
                <p class="recharge-qr-tip">支付成功后请截图联系客服确认，余额将人工到账。</p>
              </div>
            </div>
          </div>
        </transition>

        <!-- 右侧互动区域 -->
        <div class="chat-area">
          <div class="chat-tabs audience-tabs">
            <div class="chat-tab" :class="{ active: chatAudienceTab === 'chat' }" @click="chatAudienceTab = 'chat'">互动</div>
            <div class="chat-tab" :class="{ active: chatAudienceTab === 'fans' }" @click="chatAudienceTab = 'fans'">超粉</div>
            <div class="chat-tab" :class="{ active: chatAudienceTab === 'guard' }" @click="chatAudienceTab = 'guard'">守护({{ guardCount }})</div>
            <div class="chat-tab" :class="{ active: chatAudienceTab === 'vip' }" @click="chatAudienceTab = 'vip'">贵宾({{ vipCount }})</div>
            <span class="chat-online">{{ formatNumber(onlineCount) }} 在线</span>
          </div>

          <!-- 实时互动消息流 -->
          <div class="chat-content-panel">
            <div class="chat-sub-tabs">
              <div class="chat-sub-tab" :class="{ active: chatTab === 'live' }" @click="chatTab = 'live'">实时互动</div>
              <div class="chat-sub-tab" :class="{ active: chatTab === 'comment' }" @click="chatTab = 'comment'">评论 {{ commentTotal }}</div>
            </div>
            <template v-if="chatTab === 'live'">
              <div class="chat-messages live-stream" ref="liveStream">
                <div class="message-item system-message">
                  <span class="badge-noble" style="background:linear-gradient(135deg,#16a34a,#22c55e);font-size:10px;padding:0 5px;border-radius:3px;margin-right:4px;color:#fff;font-weight:700;">系统</span>
                  <span class="message-content">依法对直播内容进行24小时巡查，禁止传播违法违规、低俗色情、招嫖诈骗等不良信息，禁止未成年人直播或打赏。</span>
                </div>
                <div v-for="msg in liveMessages" :key="msg.id" class="live-msg" :class="'live-msg-' + msg.type">
                  <template v-if="msg.type === 'enter'">
                    <img v-if="msg.noble_level > 0 && getNobleIcon(msg.noble_level)" :src="getNobleIcon(msg.noble_level)" class="chat-noble-icon" :title="'贵族Lv' + msg.noble_level" alt="" @error="e => e.target.style.display = 'none'">
                    <span v-else-if="msg.noble_level > 0" class="badge-noble">贵{{ msg.noble_level }}</span>
                    <span class="fan-badge" v-if="msg.fan_level > 0">
                      <span class="fan-badge-name">{{ msg.fan_badge_name }}</span>
                      <img v-if="msg.fan_level >= 1 && msg.fan_level <= 20" :src="`/fan_badge/fan_${msg.fan_level}.png`" class="chat-fan-badge" :title="'粉丝' + msg.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <img v-else-if="msg.fan_level >= 21 && msg.fan_level <= 50" :src="`/fan_badge/fan_${msg.fan_level}.webp`" class="chat-fan-badge" :title="'粉丝' + msg.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <span v-else class="fan-badge-lv">{{ msg.fan_level }}</span>
                    </span>
                    <span class="msg-nick enter-nick">{{ msg.nickname }}</span>
                    <span class="msg-text enter-text">进入了直播间</span>
                  </template>
                  <template v-else-if="msg.type === 'gift'">
                    <span class="fan-badge" v-if="msg.fan_level > 0">
                      <span class="fan-badge-name">{{ msg.fan_badge_name }}</span>
                      <img v-if="msg.fan_level >= 1 && msg.fan_level <= 20" :src="`/fan_badge/fan_${msg.fan_level}.png`" class="chat-fan-badge" :title="'粉丝' + msg.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <img v-else-if="msg.fan_level >= 21 && msg.fan_level <= 50" :src="`/fan_badge/fan_${msg.fan_level}.webp`" class="chat-fan-badge" :title="'粉丝' + msg.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <span v-else class="fan-badge-lv">{{ msg.fan_level }}</span>
                    </span>
                    <span class="msg-nick gift-nick">{{ msg.nickname }}</span>
                    <span class="msg-text gift-text">送出</span>
                    <img v-if="msg.gift_cover" :src="fullUrl(msg.gift_cover)" class="msg-gift-img" alt="">
                    <span class="gift-name-text">{{ msg.gift_name }}</span>
                    <span class="gift-count-text">x{{ msg.gift_count }}</span>
                  </template>
                  <template v-else-if="msg.type === 'like'">
                    <span class="fan-badge" v-if="msg.fan_level > 0">
                      <span class="fan-badge-name">{{ msg.fan_badge_name }}</span>
                      <img v-if="msg.fan_level >= 1 && msg.fan_level <= 20" :src="`/fan_badge/fan_${msg.fan_level}.png`" class="chat-fan-badge" :title="'粉丝' + msg.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <img v-else-if="msg.fan_level >= 21 && msg.fan_level <= 50" :src="`/fan_badge/fan_${msg.fan_level}.webp`" class="chat-fan-badge" :title="'粉丝' + msg.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <span v-else class="fan-badge-lv">{{ msg.fan_level }}</span>
                    </span>
                    <span class="msg-nick like-nick">{{ msg.nickname }}</span>
                    <span class="msg-text like-text">点亮了直播间 ❤</span>
                  </template>
                  <template v-else-if="msg.type === 'system'">
                    <span class="msg-text sys-text">{{ msg.content }}</span>
                  </template>
                  <template v-else>
                    <img v-if="msg.noble_level > 0 && getNobleIcon(msg.noble_level)" :src="getNobleIcon(msg.noble_level)" class="chat-noble-icon" :title="'贵族Lv' + msg.noble_level" alt="" @error="e => e.target.style.display = 'none'">
                    <span v-else-if="msg.noble_level > 0" class="badge-noble">贵{{ msg.noble_level }}</span>
                    <span class="fan-badge" v-if="msg.fan_level > 0">
                      <span class="fan-badge-name">{{ msg.fan_badge_name }}</span>
                      <img v-if="msg.fan_level >= 1 && msg.fan_level <= 20" :src="`/fan_badge/fan_${msg.fan_level}.png`" class="chat-fan-badge" :title="'粉丝' + msg.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <img v-else-if="msg.fan_level >= 21 && msg.fan_level <= 50" :src="`/fan_badge/fan_${msg.fan_level}.webp`" class="chat-fan-badge" :title="'粉丝' + msg.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <span v-else class="fan-badge-lv">{{ msg.fan_level }}</span>
                    </span>
                    <span class="msg-nick">{{ msg.nickname }}：</span>
                    <span class="msg-text">{{ msg.content }}</span>
                  </template>
                </div>
                <div v-if="!liveMessages.length" class="live-empty">还没有人说话，来抢个沙发吧</div>
              </div>
            </template>

            <!-- 视频评论 -->
            <template v-else>
              <div class="chat-messages" ref="chatMessages" @scroll="handleScroll">
                <div v-for="comment in comments" :key="comment.id" class="message-item">
                  <div class="message-user">
                    <img :src="comment.user.avatar" class="user-avatar" alt="avatar" @error="handleAvatarError">
                    <span class="fan-badge" v-if="comment.fan_level > 0">
                      <span class="fan-badge-name">{{ comment.fan_badge_name }}</span>
                      <img v-if="comment.fan_level >= 1 && comment.fan_level <= 20" :src="`/fan_badge/fan_${comment.fan_level}.png`" class="chat-fan-badge" :title="'粉丝' + comment.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <img v-else-if="comment.fan_level >= 21 && comment.fan_level <= 50" :src="`/fan_badge/fan_${comment.fan_level}.webp`" class="chat-fan-badge" :title="'粉丝' + comment.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="">
                      <span v-else class="fan-badge-lv">{{ comment.fan_level }}</span>
                    </span>
                    <span class="user-name">{{ comment.user.nickname }}</span>
                  </div>
                  <div class="message-body">
                    <div class="message-content">{{ comment.content }}</div>
                    <div class="message-info">
                      <span class="message-time">{{ formatTime(comment.createtime) }}</span>
                      <span class="message-likes">{{ comment.likes }} 赞</span>
                    </div>
                  </div>
                </div>
                <div v-if="loadingComments" class="loading-more">加载中...</div>
                <div v-if="!loadingComments && comments.length >= commentTotal" class="no-more">没有更多评论了</div>
              </div>
              <div class="chat-input-area">
                <div class="input-wrapper">
                  <textarea v-model="messageInput" @keyup.enter.ctrl="sendMessage" placeholder="发表评论..." maxlength="200" rows="3"></textarea>
                  <div class="input-footer">
                    <span class="input-tip">Ctrl + Enter 发送</span>
                    <span class="input-limit" :class="{ 'limit-warning': messageInput.length >= 180 }">{{ messageInput.length }}/200</span>
                  </div>
                </div>
                <button class="send-btn" @click="sendMessage" :disabled="!messageInput.trim()">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                  <span class="send-text">发送</span>
                </button>
              </div>
            </template>
          </div>

          <!-- 观众列表 -->
          <div class="audience-content-panel" :class="{ 'is-hidden': chatAudienceTab === 'chat' }">
            <div class="audience-list" ref="audienceList" @scroll="handleAudienceScroll">
              <div v-for="user in filteredAudience" :key="user.id" class="audience-item" :class="{ 'is-noble': user.noble_level > 0 }">
                <div class="audience-info">
                  <span class="audience-info-line">
                    <img v-if="user.noble_level > 0 && getNobleIcon(user.noble_level)" :src="getNobleIcon(user.noble_level)" alt="" class="audience-noble-icon" :title="'贵族Lv' + user.noble_level">
                    <span class="audience-name">{{ user.nickname || '未知用户' }}</span>
                  </span>
                  <span class="audience-tag" v-if="user.tag">{{ user.tag }}</span>
                </div>
                <div class="audience-right">
                  <span class="audience-week-reward">本周 {{ formatGiftAmount(user.week_reward) }}金币</span>
                  <span v-if="user.fan_level > 0" class="audience-fan-wrap">
                    <span class="audience-fan-name">{{ user.fan_badge_name }}</span>
                    <img v-if="user.fan_level >= 1 && user.fan_level <= 20" :src="`/fan_badge/fan_${user.fan_level}.png`" class="audience-fan-badge" :title="'粉丝' + user.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="粉丝牌">
                    <img v-else-if="user.fan_level >= 21 && user.fan_level <= 50" :src="`/fan_badge/fan_${user.fan_level}.webp`" class="audience-fan-badge" :title="'粉丝' + user.fan_level + '级'" @error="e => e.target.style.display = 'none'" alt="粉丝牌">
                    <span v-else class="audience-fan-lv">{{ user.fan_level }}级</span>
                  </span>
                  <span v-else class="audience-level" :class="'level-' + (user.live_level || 1)">Lv{{ user.live_level || 1 }}</span>
                </div>
              </div>
              <div v-if="!filteredAudience.length" class="live-empty">暂无{{ audienceTabTitle }}</div>
            </div>
          </div>

          <!-- 青少年模式：禁止发送弹幕，替换为说明（后端亦已拦截） -->
          <div class="live-input-area minor-chat-off" v-if="minorMode">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                 stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
              <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z"/>
              <path d="M9 12h6"/>
            </svg>
            <span>青少年模式下不可发送弹幕</span>
          </div>
          <!-- 弹幕输入模块：切换顶部tab（互动/超粉/守护/贵宾）时均常驻底部 -->
          <div class="live-input-area" v-else>
            <div class="chat-dm-options">
              <div class="dm-color-picker">
                <span v-for="c in danmakuColors" :key="c" class="dm-color" :class="{ active: danmakuForm.color === c }" :style="{ background: c }" @click="danmakuForm.color = c"></span>
              </div>
              <div class="dm-position-picker">
                <button v-for="p in danmakuPositions" :key="p.value" class="chat-dm-chip" :class="{ active: danmakuForm.position === p.value }" @click="danmakuForm.position = p.value">{{ p.label }}</button>
              </div>
            </div>
            <div class="live-input-row">
              <input class="live-input" type="text" v-model="liveInput" maxlength="100" placeholder="发个弹幕吧~" @keyup.enter="handleSendLiveChat">
              <button class="live-send-btn" :disabled="!liveInput.trim() || sendingChat" @click="handleSendLiveChat">{{ sendingChat ? '发送中' : '发送' }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 举报弹窗 -->
    <div class="report-modal-mask" v-if="report.show" @click.self="closeReport">
      <div class="report-modal">
        <h3 class="report-modal-title">举报该视频</h3>
        <div class="report-reasons">
          <button v-for="r in reportReasons" :key="r" class="report-reason-btn" :class="{ active: report.reason === r }" @click="report.reason = r">{{ r }}</button>
        </div>
        <textarea v-model="report.content" class="report-textarea" placeholder="补充说明（选填）" maxlength="300" rows="3"></textarea>
        <input v-model="report.contact" class="report-contact" type="text" placeholder="联系方式（选填）" maxlength="100">
        <div class="report-actions">
          <button class="report-cancel" @click="closeReport">取消</button>
          <button class="report-submit" @click="submitReport" :disabled="report.submitting">{{ report.submitting ? '提交中...' : '提交举报' }}</button>
        </div>
      </div>
    </div>

    <PageFooter />
  </div>
</template>

<script>
import { getVideoDetail, getVideoComments, addVideoComment, getGiftList, rewardGift } from '../api/video';
import { submitReport } from '../api/report';
import { follow, unfollow, getFollowStatus } from '../api/follow';
import { getDanmakuList, sendDanmaku } from '../api/danmaku';
import { pullLiveChat, sendLiveChat, enterLiveRoom, liveHeartbeat, likeLiveRoom, getLiveAudience } from '../api/liveChat';
import { getRankList } from '../api/rank';
import { sendRedPacket, grabRedPacket, getPacketList } from '../api/redPacket';
import { createLottery, joinLottery, drawLottery, getCurrentLottery } from '../api/lottery';
import { startPk, acceptPk, rejectPk, getPkInfo, endPk, startMic, endMic, getMicList } from '../api/livePk';
import { rechargePackages as fetchRechargePackages } from '../api/recharge';
import erweimaImg from '../assets/erweima.jpg';
import { getNobleLevels, getMyNoble, buyNoble } from '../api/noble';
import { getGuardList, buyGuard, joinFanClub, getMyBadges } from '../api/title';
import { getMyTreasures, openTreasure, getTreasureStatus } from '../api/treasure';
import { getItemList, getMyItems, buyItem, useItem } from '../api/shop';
import { BASE_URL } from '../api/config';
import { addWatchHistory } from '../api/watchHistory';
import { avatarFallback } from '../utils/avatar';
import { getRealnameStatus } from '../api/realname';
import { confirmDialog } from './ConfirmDialog.vue';
import SVGA from 'svgaplayerweb';
import DanmakuLayer from './DanmakuLayer.vue';
import PageFooter from './Footer.vue';

export default {
  name: 'LiveDetail',
  components: {
    PageFooter,
    DanmakuLayer
  },
  data() {
    return {
      videoDetail: null,
      videoError: false,
      defaultAvatar: require('../assets/logo.png'),
      messageInput: '',
      volume: 50,
      lastVolume: 50,
      isPlaying: true,
      comments: [],
      commentPage: 1,
      commentLimit: 10,
      commentTotal: 0,
      loadingComments: false,
      giftList: [],
      selectedGift: null,
      giftCount: 1,
      isRewarding: false,
      svgaPlayer: null,
      svgaParser: null,
      pngEffect: { show: false, url: '', duration: 2500, timer: null },
      toast: {
        show: false,
        message: '',
        type: 'success',
        timer: null
      },
      videoAspectRatio: 16 / 9,
      isVerticalVideo: false,
      report: {
        show: false,
        reason: '',
        content: '',
        contact: '',
        submitting: false
      },
      reportReasons: ['色情低俗', '违法违规', '诈骗信息', '侵权抄袭', '暴力血腥', '其他'],
      isFollowed: false,
      isLoggedIn: !!localStorage.getItem('token'),
      minorMode: false,

      danmakuList: [],
      videoCurrentTime: 0,
      showDanmakuSetting: false,
      sendingDanmaku: false,
      danmakuSetting: {
        enable: true,
        opacity: 1,
        duration: 10,
        scale: 1,
        tracks: 8
      },
      danmakuForm: {
        content: '',
        color: '#ffffff',
        position: 'scroll',
        fontsize: 24
      },
      danmakuColors: ['#ffffff', '#ff6b6b', '#4ecdc4', '#ffe66d', '#a78bfa', '#38bdf8'],
      danmakuPositions: [
        { value: 'scroll', label: '滚动' },
        { value: 'top', label: '顶部' },
        { value: 'bottom', label: '底部' }
      ],
      fontScales: [
        { value: 0.8, label: '小' },
        { value: 1, label: '中' },
        { value: 1.25, label: '大' }
      ],
      trackOptions: [
        { value: 4, label: '半屏' },
        { value: 8, label: '3/4' },
        { value: 12, label: '全屏' }
      ],

      chatTab: 'live',
      chatAudienceTab: 'chat',
      liveMessages: [],
      liveInput: '',
      sendingChat: false,
      lastChatId: 0,
      onlineCount: 0,
      heat: 0,
      likeCount: 0,
      chatTimer: null,
      heartbeatTimer: null,

      // 观众列表
      audienceList: [],
      audiencePage: 1,
      audienceLoading: false,
      audienceHasMore: true,

      giftBanners: [],
      hearts: [],
      effectSeed: 0,
      // 进场特效
      enterEffect: { show: false, nickname: '', avatar: '', noble_level: 0, fan_level: 0, fan_badge_name: '', key: 0 },
      enterEffectTimer: null,
      firstPull: true,
      pullingChat: false,
      // 用于清理的 setTimeout 句柄
      svgaSafetyTimer: null,
      heartTimers: [],
      bannerTimers: [],
      handleVideoLoaded: null,

      // 互动功能状态
      userId: (() => {
        try {
          const info = JSON.parse(localStorage.getItem('userInfo') || '{}');
          return Number(info.id) || 0;
        } catch (e) {
          return 0;
        }
      })(),
      showRankPanel: false,
      showRedPacketPanel: false,
      showLotteryPanel: false,
      showPkPanel: false,

      // 排行榜
      rankPeriod: 'day',
      rankList: [],

      // 红包
      showSendPacketForm: false,
      packetForm: { totalAmount: '', count: '', type: 'random', message: '' },
      packetList: [],

      // 抽奖
      showCreateLotteryForm: false,
      lotteryForm: { title: '', prize: '', prizeCount: 1, duration: 300, condition: 'all' },
      currentLottery: null,

      // 连麦 / PK
      pkTab: 'pk',
      pkForm: { targetRoomId: '', duration: 300 },
      currentPk: null,
      micForm: { targetUserId: '' },
      micList: [],

      // 工具栏面板
      toolbarPanel: '',
      showNobleModal: false,
      showGuardModal: false,
      showToolbarModal: '',
      quickGiftCount: 1,
      rechargePackagesList: [],
      rechargeQR: { show: false, amount: 0, url: '' },
      bagItems: [],
      nobleLevels: [],
      myNoble: null,
      guardList: [],
      myGuard: null,
      fanClubJoined: false,
      myTreasures: [],
      treasureStatus: null,
      openingTreasure: false,
      shopItems: [],
      shopTab: 'items',
      bagItemUsing: false,
      videoQuality: '480',
      showQualityPanel: false
    }
  },
  computed: {
    videoUrl() {
      if (!this.videoDetail?.video_url) return '';
      const url = this.videoDetail.video_url;
      let finalUrl = url;
      if (!url.startsWith('http')) {
        finalUrl = `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
      }
      return finalUrl;
    },
    videoPoster() {
      const cover = this.videoDetail?.cover || this.videoDetail?.image || this.videoDetail?.thumbnail || '';
      if (!cover) return this.defaultAvatar;
      if (cover.startsWith('http') || cover.startsWith('data:')) return cover;
      return `${BASE_URL}${cover.startsWith('/') ? '' : '/'}${cover}`;
    },
    authorAvatar() {
      if (!this.videoDetail?.user?.avatar) return avatarFallback(this.videoDetail?.user?.nickname || '主播', this.videoDetail?.user_id);
      const url = this.videoDetail.user.avatar;
      if (url.startsWith("http") || url.startsWith("data:")) return url;
      return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
    },
    videoContainerStyle() {
      return {
        width: '100%',
        aspectRatio: `${this.videoAspectRatio}`
      };
    },
    opacityPercent: {
      get() {
        return Math.round(this.danmakuSetting.opacity * 100);
      },
      set(val) {
        this.danmakuSetting.opacity = val / 100;
      }
    },
    qualityLabel() {
      const map = { '480': '480P', '720': '720P', '1080': '1080P' };
      return map[this.videoQuality] || '480P';
    },
    isAnchor() {
      return !!(this.userId && this.videoDetail && Number(this.videoDetail.user_id) === Number(this.userId));
    },
    pkProgressA() {
      if (!this.currentPk) return 50;
      const a = Number(this.currentPk.score_a) || 0;
      const b = Number(this.currentPk.score_b) || 0;
      const total = a + b;
      if (total <= 0) return 50;
      return Math.round((a / total) * 100);
    },
    pkRemaining() {
      if (!this.currentPk || this.currentPk.status !== 1) return '00:00';
      const endAt = Number(this.currentPk.end_at || this.currentPk.end_time || 0);
      const now = Math.floor(Date.now() / 1000);
      let remain = endAt ? endAt - now : Number(this.currentPk.remaining || 0);
      if (remain <= 0) return '00:00';
      const m = Math.floor(remain / 60);
      const s = remain % 60;
      return (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s);
    },
    quickGifts() {
      return (this.giftList || []).slice(0, 6);
    },
    guardCount() {
      return this.audienceList.filter(u => u.type === 'guard').length;
    },
    vipCount() {
      return this.audienceList.filter(u => u.total_reward > 0 || u.type === 'vip' || u.type === 'guard' || u.is_fan || u.noble_level > 0).length;
    },
    filteredAudience() {
      const map = {
        fans: u => u.type === 'fans' || u.is_fan || u.fan_level > 0,
        guard: u => u.type === 'guard',
        // 贵宾席 = 贡献榜：有打赏/贵族/守护/粉丝身份的用户，按后端已排好的顺序展示
        vip: u => u.total_reward > 0 || u.type === 'vip' || u.type === 'guard' || u.is_fan || u.noble_level > 0
      };
      const filter = map[this.chatAudienceTab] || (() => true);
      return this.audienceList.filter(filter);
    },
    audienceTabTitle() {
      const titles = { fans: '超粉', guard: '守护', vip: '贵宾' };
      return titles[this.chatAudienceTab] || '';
    },
    toolbarPanelTitle() {
      const titles = {
        more: '更多',
        gift: '选择礼物',
        recharge: '充值',
        bag: '我的背包',
        noble: '贵族',
        treasure: '百宝箱',
        shop: '小黄车'
      };
      return titles[this.toolbarPanel] || '';
    },
    toolbarModalTitle() {
      const titles = {
        gift: '选择礼物',
        recharge: '充值中心',
        bag: '我的包裹',
        treasure: '百宝箱',
        shop: '小黄车'
      };
      return titles[this.showToolbarModal] || '功能面板';
    }
  },
  methods: {
    confirmDialog,
    fetchMinorMode() {
      getRealnameStatus().then(res => {
        const d = res && (res.data || res);
        this.minorMode = !!(d && (d.minor_mode === 1 || d.minorMode === 1));
      }).catch(() => { /* 默认按非青少年处理，避免遮挡界面 */ });
    },
    getFullImageUrl(url) {
      if (!url) return '';
      if (url.startsWith('http') || url.startsWith('data:')) return url;
      return `${BASE_URL}${url}`;
    },
    getNobleIcon(level) {
      if (!level) return '';
      const noble = this.nobleLevels.find(n => parseInt(n.level, 10) === parseInt(level, 10));
      if (!noble || !noble.icon) return '';
      return this.getFullImageUrl(noble.icon);
    },
    getGuardIcon(level) {
      const colors = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];
      const color = colors[(level - 1) % colors.length];
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="${color}"/><stop offset="100%" stop-color="#fff"/></linearGradient></defs><circle cx="32" cy="32" r="30" fill="url(#g)"/><path d="M32 10l6 12 13 2-9 9 2 13-12-6-12 6 2-13-9-9 13-2z" fill="#fff"/></svg>`;
      return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
    },
    handleAvatarError(e) {
      e.target.src = this.defaultAvatar;
    },
    goAnchorHome() {
      const anchorId = this.videoDetail?.user_id;
      if (!anchorId) { this.showToast('主播信息缺失', 'error'); return; }
      this.$router.push({ name: 'AnchorHome', params: { id: anchorId } });
    },
    async fetchVideoDetail() {
      try {
        const id = Number(this.$route.params.id);
        if (!id || id <= 0) {
          this.showToast('视频ID无效', 'error');
          this.$router.replace('/');
          return;
        }
        const response = await getVideoDetail(id);
        if (response && response.code === 1) {
          this.videoError = false;
          this.videoDetail = response.data;
          this.likeCount = Number(response.data.likes) || 0;
          this.heat = Number(response.data.heat) || 0;
          this.checkFollowStatus();
          this.fetchLiveInteractData();
        } else {
          this.showToast(response?.msg || '获取视频详情失败', 'error');
        }
      } catch (error) {
        this.showToast(error.message || '该视频无法观看', 'error');
      }
    },
    handleVolume() {
      const video = this.$refs.videoPlayer;
      if (video) {
        if (this.volume > 0) {
          video.muted = false;
          this.lastVolume = this.volume;
        } else {
          video.muted = true;
        }
        video.volume = this.volume / 100;
      }
    },
    toggleMute() {
      const video = this.$refs.videoPlayer;
      if (video) {
        video.muted = !video.muted;
        if (video.muted) {
          this.lastVolume = this.volume;
          this.volume = 0;
        } else {
          this.volume = this.lastVolume;
        }
        video.volume = this.volume / 100;
      }
    },
    toggleFullscreen() {
      const container = this.$refs.videoContainer;
      if (container) {
        if (!document.fullscreenElement) {
          container.requestFullscreen().catch(() => {});
        } else {
          document.exitFullscreen().catch(() => {});
        }
      }
    },
    toggleQualityPanel() {
      this.showQualityPanel = !this.showQualityPanel;
    },
    async selectQuality(q) {
      this.showQualityPanel = false;
      if (q === this.videoQuality) return;
      if (q === '480') {
        this.videoQuality = '480';
        this.showToast('已切换至 480P 流畅画质');
        return;
      }
      if (q === '720') {
        this.videoQuality = '720';
        this.showToast('已切换至 720P 高清画质');
        return;
      }
      // 1080P：需开通贵族后切换
      if (!this.isLoggedIn) {
        this.showToast('请先登录', 'error');
        this.$router.push('/login');
        return;
      }
      if (!this.myNoble) {
        try {
          const myRes = await getMyNoble();
          if (myRes && myRes.code === 1) this.myNoble = myRes.data?.current_noble || null;
        } catch (e) { /* ignore */ }
      }
      if (!this.myNoble) {
        this.showToast('1080P高清画质需开通贵族后切换');
        this.openNobleModal();
        return;
      }
      this.videoQuality = '1080';
      this.showToast('已切换至 1080P 高清画质');
    },
    handleVideoError(e) {
      this.videoError = true;
      this.isPlaying = false;
      console.warn('视频加载失败，已切换为封面图', e);
    },
    handleVideoMetadata() {
      const video = this.$refs.videoPlayer;
      if (video) {
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        if (videoWidth && videoHeight) {
          this.isVerticalVideo = videoHeight > videoWidth;
          this.videoAspectRatio = this.isVerticalVideo ? 9 / 16 : 16 / 9;
        }
      }
    },
    async sendMessage() {
      if (!this.messageInput.trim()) {
        this.showToast('请输入评论内容', 'error');
        return;
      }
      const token = localStorage.getItem('token');
      try {
        const response = await addVideoComment({
          video_id: this.$route.params.id,
          content: this.messageInput.trim(),
          token: token
        });
        if (response && response.code === 1) {
          this.messageInput = '';
          this.showToast('评论发表成功');
          this.commentPage = 1;
          this.comments = [];
          await this.fetchComments();
        } else {
          this.showToast(response?.msg || '评论发表失败', 'error');
        }
      } catch (error) {
        this.showToast('评论发表失败，请稍后重试', 'error');
      }
    },
    async fetchComments() {
      if (this.loadingComments) return;
      try {
        this.loadingComments = true;
        const response = await getVideoComments({
          video_id: this.$route.params.id,
          page: this.commentPage,
          limit: this.commentLimit
        });
        if (response.code === 1) {
          const processedComments = (response.data.list || []).map(comment => {
            const rawAvatar = (comment.user && comment.user.avatar) || '';
            let fullAvatar = rawAvatar;
            if (rawAvatar && !rawAvatar.startsWith('http') && !rawAvatar.startsWith('data:')) {
              fullAvatar = `${BASE_URL}${rawAvatar.startsWith('/') ? '' : '/'}${rawAvatar}`;
            }
            return {
              ...comment,
              user: {
                ...(comment.user || {}),
                avatar: fullAvatar
              }
            };
          });
          this.comments = [...this.comments, ...processedComments];
          this.commentTotal = response.data.total;
        }
      } catch (error) {
        console.error('获取评论失败:', error);
      } finally {
        this.loadingComments = false;
      }
    },
    loadMoreComments() {
      if (this.comments.length >= this.commentTotal) return;
      this.commentPage += 1;
      this.fetchComments();
    },
    handleScroll(e) {
      const el = e.target;
      if (!el) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        this.loadMoreComments();
      }
    },
    formatTime(timestamp) {
      const date = new Date(timestamp * 1000);
      return date.toLocaleString();
    },
    async fetchGiftList() {
      try {
        const response = await getGiftList();
        if (response && response.code === 1) {
          this.giftList = (response.data || []).map(gift => {
            const cover = gift.cover || '';
            const image = gift.image || '';
            return {
              ...gift,
              displayImage: cover.startsWith('http') ? cover : `${BASE_URL}${cover}`,
              effectUrl: image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : ''
            };
          });
        }
      } catch (error) {
        console.error('获取礼物列表失败:', error);
      }
    },
    selectGift(gift) {
      this.selectedGift = gift;
    },
    // 礼物面板：点击礼物先确认再送出，避免误触直接扣费
    async pickAndSendGift(gift) {
      if (!gift) return;
      if (!this.isLoggedIn) { this.showToast('请先登录后再送礼', 'error'); this.$router.push('/login'); return; }
      if (this.isRewarding) return;
      this.selectedGift = gift;
      this.showToolbarModal = '';
      const cost = Number(gift.price || 0) * Number(this.giftCount || 1);
      const ok = await this.confirmDialog({
        title: '确认送礼',
        message: `确定送出【${gift.name}】x${this.giftCount || 1}，消耗 ${cost} 金币？`
      });
      if (!ok) return;
      await this.sendGift();
    },
    validateGiftCount() {
      if (this.giftCount < 1) this.giftCount = 1;
      else if (this.giftCount > 99) this.giftCount = 99;
    },
    async playGiftEffect(giftUrl) {
      try {
        const fullGiftUrl = giftUrl.startsWith('http') ? giftUrl : `${BASE_URL}${giftUrl.startsWith('/') ? '' : '/'}${giftUrl}`;

        // PNG / JPG / WebP / GIF 走 CSS 动画展示（无需 SVGA 素材）
        if (/\.(png|jpe?g|webp|gif)(\?|$)/i.test(fullGiftUrl)) {
          if (this.pngEffect.timer) { clearTimeout(this.pngEffect.timer); this.pngEffect.timer = null; }
          this.pngEffect.url = fullGiftUrl;
          this.pngEffect.show = true;
          this.pngEffect.timer = setTimeout(() => {
            this.pngEffect.show = false;
            this.pngEffect.timer = null;
          }, this.pngEffect.duration);
          return;
        }

        // SVGA 特效走原逻辑
        if (!this.svgaPlayer || !this.svgaParser) {
          const canvas = document.getElementById('gift-effect-canvas');
          this.svgaPlayer = new SVGA.Player(canvas);
          this.svgaParser = new SVGA.Parser();
          canvas.width = canvas.offsetWidth;
          canvas.height = canvas.offsetHeight;
        }
        this.svgaPlayer.stopAnimation();
        const videoItem = await new Promise((resolve, reject) => {
          this.svgaParser.load(fullGiftUrl, (videoItem) => resolve(videoItem), (error) => reject(error));
        });
        await this.svgaPlayer.setVideoItem(videoItem);
        this.svgaPlayer.startAnimation();
        this.svgaPlayer.onFinished(() => { this.svgaPlayer.stopAnimation(); });
        if (this.svgaSafetyTimer) { clearTimeout(this.svgaSafetyTimer); }
        this.svgaSafetyTimer = setTimeout(() => { if (this.svgaPlayer) this.svgaPlayer.stopAnimation(); this.svgaSafetyTimer = null; }, 6000);
      } catch (error) {
        console.error('播放礼物特效失败:', error);
      }
    },
    formatGiftAmount(amount) {
      const value = Number(amount) || 0;
      return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    },
    showToast(message, type = 'success') {
      if (this.toast.timer) clearTimeout(this.toast.timer);
      this.toast.show = true;
      this.toast.message = message;
      this.toast.type = type;
      this.toast.timer = setTimeout(() => { this.toast.show = false; }, 2000);
    },
    async sendGift() {
      if (!this.selectedGift) { this.showToast('请先选择礼物', 'error'); return; }
      try {
        this.isRewarding = true;
        const response = await rewardGift({ video_id: this.$route.params.id, gift_id: this.selectedGift.id, gift_count: this.giftCount });
        if (response.code === 1) {
          this.showToast('打赏成功');
          this.pushGiftBanner({ nickname: '我', gift_name: this.selectedGift.name, gift_count: this.giftCount, gift_cover: this.selectedGift.displayImage });
          this.pullChat();
          if (this.selectedGift.effectUrl) {
            await this.playGiftEffect(this.selectedGift.effectUrl);
          }
        } else {
          this.showToast(response.msg || '打赏失败', 'error');
        }
      } catch (error) {
        // request.js 拦截器在 code !== 1 时会 reject(new Error(msg))，
        // 这里取出后端返回的错误消息（如"余额不足，请先充值"），无消息时回退到通用提示
        const msg = (error && error.message) || '打赏失败，请稍后重试';
        this.showToast(msg, 'error');
      } finally {
        this.isRewarding = false;
      }
    },
    openReport() {
      const token = localStorage.getItem('token');
      if (!token) { this.showToast('请先登录后再举报', 'error'); return; }
      this.report.show = true;
      this.report.reason = '';
      this.report.content = '';
      this.report.contact = '';
      this.report.submitting = false;
    },
    closeReport() { this.report.show = false; },
    async submitReport() {
      if (!this.report.reason) { this.showToast('请选择举报原因', 'error'); return; }
      this.report.submitting = true;
      try {
        const response = await submitReport({ targetType: 'video', targetId: this.$route.params.id, reason: this.report.reason, content: this.report.content, contact: this.report.contact });
        if (response && response.code === 1) {
          this.showToast('举报已提交，感谢您的反馈');
          this.report.show = false;
        } else {
          this.showToast(response?.msg || '举报提交失败', 'error');
        }
      } catch (error) {
        this.showToast(error?.message || '举报提交失败，请稍后重试', 'error');
      } finally {
        this.report.submitting = false;
      }
    },
    async checkFollowStatus() {
      if (!this.isLoggedIn || !this.videoDetail?.user_id) return;
      try {
        const res = await getFollowStatus(this.videoDetail.user_id);
        if (res && res.code === 1) { this.isFollowed = !!(res.data && res.data.is_follow); }
      } catch (_) { /* ignore */ }
    },
    async toggleFollow() {
      if (!this.isLoggedIn) { this.$router.push('/login'); return; }
      try {
        if (this.isFollowed) {
          const res = await unfollow(this.videoDetail.user_id);
          if (res && res.code === 1) { this.isFollowed = false; this.showToast('已取消关注'); }
        } else {
          const res = await follow(this.videoDetail.user_id);
          if (res && res.code === 1) { this.isFollowed = true; this.showToast('关注成功'); }
        }
      } catch (error) {
        this.showToast(error?.message || '操作失败', 'error');
      }
    },
    destroyGiftEffect() {
      if (this.svgaPlayer) {
        this.svgaPlayer.stopAnimation(true);
        this.svgaPlayer.clearDynamicObjects();
        this.svgaPlayer.clear();
        this.svgaPlayer = null;
        this.svgaParser = null;
      }
      if (this.svgaSafetyTimer) { clearTimeout(this.svgaSafetyTimer); this.svgaSafetyTimer = null; }
      if (this.pngEffect.timer) { clearTimeout(this.pngEffect.timer); this.pngEffect.timer = null; }
      this.pngEffect.show = false;
    },
    fullUrl(url) {
      if (!url) return '';
      if (url.startsWith('http') || url.startsWith('data:')) return url;
      return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
    },
    formatNumber(num) {
      const n = Number(num) || 0;
      if (n >= 10000) return (n / 10000).toFixed(1) + '万';
      return String(n);
    },
    handleTimeUpdate() {
      const video = this.$refs.videoPlayer;
      if (!video) return;
      this.videoCurrentTime = video.currentTime || 0;
    },
    toggleDanmaku() {
      this.danmakuSetting.enable = !this.danmakuSetting.enable;
      this.showToast(this.danmakuSetting.enable ? '弹幕已开启' : '弹幕已关闭');
    },
    async fetchDanmakuList() {
      try {
        const res = await getDanmakuList({ video_id: this.$route.params.id, limit: 1000 });
        if (res && res.code === 1) { this.danmakuList = res.data.list || []; }
      } catch (error) { console.warn('获取弹幕失败:', error?.message); }
    },
    async handleSendDanmaku() {
      const content = this.danmakuForm.content.trim();
      if (!content) return;
      if (this.minorMode) { this.showToast('青少年模式下不可发送弹幕', 'error'); return; }
      if (!this.isLoggedIn) { this.showToast('请先登录后再发弹幕', 'error'); this.$router.push('/login'); return; }
      this.sendingDanmaku = true;
      try {
        const video = this.$refs.videoPlayer;
        const res = await sendDanmaku({ video_id: this.$route.params.id, content, video_time: video ? Number((video.currentTime || 0).toFixed(2)) : 0, color: this.danmakuForm.color, position: this.danmakuForm.position, fontsize: this.danmakuForm.fontsize });
        if (res && res.code === 1) {
          const row = res.data;
          // 确保使用用户选择的位置（后端可能未正确返回position）
          row.position = this.danmakuForm.position;
          this.danmakuList.push(row);
          if (this.$refs.danmakuLayer) {
            this.$refs.danmakuLayer.pushNow(row);
          }
          this.danmakuForm.content = '';
        } else { this.showToast(res?.msg || '弹幕发送失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '弹幕发送失败', 'error'); }
      finally { this.sendingDanmaku = false; }
    },
    async pullChat() {
      if (this.pullingChat) return;
      this.pullingChat = true;
      try {
        const res = await pullLiveChat({ video_id: this.$route.params.id, since_id: this.lastChatId, limit: 50 });
        if (!res || res.code !== 1) return;
        const { list = [], max_id: maxId, online } = res.data;
        if (typeof online === 'number') this.onlineCount = online;
        if (maxId && maxId > this.lastChatId) this.lastChatId = maxId;
        if (list.length) {
          list.forEach(msg => {
            this.liveMessages.push(msg);
            if (msg.type === 'gift') { this.pushGiftBanner(msg); }
            else if (msg.type === 'enter' && !this.firstPull) { this.playEnterEffect(msg); }
          });
          if (this.liveMessages.length > 200) { this.liveMessages.splice(0, this.liveMessages.length - 200); }
          this.mergeAudienceFromMessages();
          this.scrollLiveStreamToBottom();
        }
        this.firstPull = false;
      } catch (error) { console.warn('拉取直播间消息失败:', error?.message); }
      finally { this.pullingChat = false; }
    },
    scrollLiveStreamToBottom() {
      this.$nextTick(() => { const el = this.$refs.liveStream; if (el) el.scrollTop = el.scrollHeight; });
    },
    playEnterEffect(msg) {
      if (this.enterEffectTimer) { clearTimeout(this.enterEffectTimer); }
      this.enterEffect.show = true;
      this.enterEffect.nickname = msg.nickname || '神秘用户';
      this.enterEffect.avatar = msg.avatar || this.defaultAvatar;
      this.enterEffect.noble_level = msg.noble_level || 0;
      this.enterEffect.fan_level = msg.fan_level || 0;
      this.enterEffect.fan_badge_name = msg.fan_badge_name || '';
      this.enterEffect.key = ++this.effectSeed;
      this.$forceUpdate();
      this.enterEffectTimer = setTimeout(() => { this.enterEffect.show = false; }, 3200);
    },
    handleEnterAvatarError() {
      this.enterEffect.avatar = this.defaultAvatar;
    },
    async handleSendLiveChat() {
      const content = this.liveInput.trim();
      if (!content) return;
      if (this.minorMode) { this.showToast('青少年模式下不可发送弹幕', 'error'); return; }
      if (!this.isLoggedIn) { this.showToast('请先登录后再发言', 'error'); this.$router.push('/login'); return; }
      this.sendingChat = true;
      try {
        const videoId = this.$route.params.id;
        const res = await sendLiveChat({ video_id: videoId, content });
        if (res && res.code === 1) {
          this.liveInput = '';
          await this.pullChat();
          // 虎牙模式：右侧发送的消息同时作为飞屏弹幕从视频画面飞过
          try {
            const video = this.$refs.videoPlayer;
            const dmRes = await sendDanmaku({ video_id: videoId, content, video_time: video ? Number((video.currentTime || 0).toFixed(2)) : 0, color: this.danmakuForm.color, position: this.danmakuForm.position, fontsize: this.danmakuForm.fontsize, skip_chat: 1 });
            if (dmRes && dmRes.code === 1) {
              const row = dmRes.data;
              // 确保使用用户选择的位置（后端可能未正确返回position）
              row.position = this.danmakuForm.position;
              this.danmakuList.push(row);
              if (this.$refs.danmakuLayer) { this.$refs.danmakuLayer.pushNow(row); }
            }
          } catch (dmError) { console.warn('飞屏弹幕同步失败:', dmError?.message); }
        }
        else { this.showToast(res?.msg || '发送失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '发送失败', 'error'); }
      finally { this.sendingChat = false; }
    },
    async enterRoom() {
      try {
        const res = await enterLiveRoom(this.$route.params.id);
        if (res && res.code === 1 && res.data) { this.onlineCount = res.data.online || 0; }
      } catch (error) { console.warn('进入直播间失败:', error?.message); }
    },
    async sendHeartbeat() {
      try {
        const res = await liveHeartbeat(this.$route.params.id);
        if (res && res.code === 1 && res.data) { this.onlineCount = res.data.online || 0; }
      } catch (_) { /* ignore */ }
      // 心跳同步刷新在线观众（含贵族/守护身份），保证贵宾列表实时
      if (this.chatAudienceTab !== 'chat') {
        this.refreshAudienceList();
      }
    },
    async handleLike() {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); return; }
      this.likeCount += 1;
      this.pushHeart();
      try {
        const res = await likeLiveRoom(this.$route.params.id, 1);
        if (res && res.code === 1 && res.data && typeof res.data.likes === 'number') { this.likeCount = res.data.likes; }
      } catch (error) { this.likeCount = Math.max(0, this.likeCount - 1); }
    },

    async fetchAudienceList(reset = true) {
      if (this.audienceLoading) return;
      if (reset) {
        this.audiencePage = 1;
        this.audienceHasMore = true;
      }
      this.audienceLoading = true;
      try {
        const res = await getLiveAudience({ video_id: this.$route.params.id, page: this.audiencePage, limit: 20 });
        if (res && res.code === 1) {
          const list = res.data?.list || [];
          if (reset) {
            // 原子替换，避免先清空导致的列表闪烁
            this.audienceList = list;
          } else {
            const known = new Set(this.audienceList.map(u => Number(u.id)));
            this.audienceList.push(...list.filter(u => !known.has(Number(u.id))));
          }
          const total = Number(res.data?.total || 0);
          this.audienceHasMore = list.length >= 20 && this.audienceList.length < total;
        }
      } catch (error) { console.warn('获取观众列表失败:', error?.message); }
      finally { this.audienceLoading = false; }
    },
    // 心跳静默刷新：合并更新已有项字段，仅增删真实变化的用户，避免列表整体重绘闪烁
    async refreshAudienceList() {
      if (this.audienceLoading) return;
      this.audienceLoading = true;
      try {
        const res = await getLiveAudience({ video_id: this.$route.params.id, page: 1, limit: 50 });
        if (res && res.code === 1) {
          const fresh = res.data?.list || [];
          const freshMap = new Map(fresh.map(u => [Number(u.id), u]));
          const kept = this.audienceList
            .filter(u => freshMap.has(Number(u.id)))
            .map(u => ({ ...u, ...freshMap.get(Number(u.id)) }));
          const known = new Set(kept.map(u => Number(u.id)));
          fresh.forEach(u => {
            const id = Number(u.id);
            if (!known.has(id)) { kept.push(u); known.add(id); }
          });
          this.audienceList = kept;
        }
      } catch (error) { /* ignore */ }
      finally { this.audienceLoading = false; }
    },
    handleAudienceScroll() {
      const el = this.$refs.audienceList;
      if (!el || this.audienceLoading || !this.audienceHasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        this.audiencePage += 1;
        this.fetchAudienceList(false);
      }
    },
    mergeAudienceFromMessages() {
      this.liveMessages.forEach(msg => {
        if (!msg.user_id || !msg.nickname) return;
        const exists = this.audienceList.find(u => u.id === msg.user_id);
        if (!exists) {
          this.audienceList.unshift({
            id: msg.user_id,
            nickname: msg.nickname,
            avatar: msg.avatar || this.defaultAvatar,
            level: msg.live_level || 1,
            type: msg.noble_level > 0 ? 'vip' : 'normal',
            noble_level: msg.noble_level || 0,
            tag: ''
          });
        }
      });
    },
    handleDoubleLike() {
      for (let i = 0; i < 3; i++) {
        const t = setTimeout(() => {
          this.pushHeart();
          const idx = this.heartTimers.indexOf(t);
          if (idx >= 0) this.heartTimers.splice(idx, 1);
        }, i * 120);
        this.heartTimers.push(t);
      }
      this.handleLike();
    },
    pushHeart() {
      const key = ++this.effectSeed;
      const left = 20 + Math.random() * 60;
      const drift = (Math.random() * 60 - 30).toFixed(0);
      this.hearts.push({ key, style: { left: left + '%', '--drift': drift + 'px', color: ['#ff4d6d', '#ff6b6b', '#ffa8b6'][key % 3] } });
      const t = setTimeout(() => {
        this.hearts = this.hearts.filter(h => h.key !== key);
        const idx = this.heartTimers.indexOf(t);
        if (idx >= 0) this.heartTimers.splice(idx, 1);
      }, 2200);
      this.heartTimers.push(t);
    },
    pushGiftBanner(msg) {
      const key = ++this.effectSeed;
      this.giftBanners.push({ key, nickname: msg.nickname || '神秘用户', giftName: msg.gift_name || '礼物', count: msg.gift_count || 1, cover: msg.gift_cover ? this.fullUrl(msg.gift_cover) : '' });
      if (this.giftBanners.length > 3) this.giftBanners.shift();
      const t = setTimeout(() => {
        this.giftBanners = this.giftBanners.filter(b => b.key !== key);
        const idx = this.bannerTimers.indexOf(t);
        if (idx >= 0) this.bannerTimers.splice(idx, 1);
      }, 5000);
      this.bannerTimers.push(t);
    },
    startPolling() {
      this.stopPolling();
      this.chatTimer = setInterval(() => { this.pullChat(); }, 3000);
      this.heartbeatTimer = setInterval(() => { this.sendHeartbeat(); }, 20000);
    },
    stopPolling() {
      if (this.chatTimer) { clearInterval(this.chatTimer); this.chatTimer = null; }
      if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
    },
    handleImgError(e) {
      e.target.src = this.defaultAvatar;
    },
    async fetchRankList() {
      try {
        const res = await getRankList({ room_id: this.$route.params.id, type: 'contribution', period: this.rankPeriod });
        if (res && res.code === 1) { this.rankList = res.data?.list || res.data || []; }
      } catch (error) { console.warn('获取排行榜失败:', error?.message); }
    },
    async fetchPacketList() {
      try {
        const res = await getPacketList(this.$route.params.id);
        if (res && res.code === 1) { this.packetList = res.data?.list || res.data || []; }
      } catch (error) { console.warn('获取红包列表失败:', error?.message); }
    },
    async handleSendPacket() {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      const totalAmount = Number(this.packetForm.totalAmount);
      const count = Number(this.packetForm.count);
      if (!totalAmount || totalAmount < 0.01) { this.showToast('请输入正确的总金额', 'error'); return; }
      if (!count || count < 1) { this.showToast('请输入红包个数', 'error'); return; }
      try {
        const res = await sendRedPacket({
          room_id: this.$route.params.id,
          total_amount: totalAmount,
          count: count,
          type: this.packetForm.type,
          message: this.packetForm.message
        });
        if (res && res.code === 1) {
          this.showToast('红包发送成功');
          this.packetForm = { totalAmount: '', count: '', type: 'random', message: '' };
          this.showSendPacketForm = false;
          await this.fetchPacketList();
        } else { this.showToast(res?.msg || '红包发送失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '红包发送失败', 'error'); }
    },
    async handleGrabPacket(redpacketId) {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      try {
        const res = await grabRedPacket(redpacketId);
        if (res && res.code === 1) {
          const amount = res.data?.amount || 0;
          this.showToast('抢到 ¥' + amount);
          await this.fetchPacketList();
        } else { this.showToast(res?.msg || '抢红包失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '抢红包失败', 'error'); }
    },
    async fetchCurrentLottery() {
      try {
        const res = await getCurrentLottery(this.$route.params.id);
        if (res && res.code === 1) { this.currentLottery = res.data || null; }
      } catch (error) { console.warn('获取抽奖信息失败:', error?.message); }
    },
    async handleCreateLottery() {
      if (!this.lotteryForm.title.trim()) { this.showToast('请输入抽奖标题', 'error'); return; }
      if (!this.lotteryForm.prize.trim()) { this.showToast('请输入奖品名称', 'error'); return; }
      try {
        const res = await createLottery({
          room_id: this.$route.params.id,
          title: this.lotteryForm.title.trim(),
          prize: this.lotteryForm.prize.trim(),
          prize_count: Number(this.lotteryForm.prizeCount) || 1,
          duration: Number(this.lotteryForm.duration) || 300,
          condition: this.lotteryForm.condition
        });
        if (res && res.code === 1) {
          this.showToast('抽奖已发起');
          this.lotteryForm = { title: '', prize: '', prizeCount: 1, duration: 300, condition: 'all' };
          this.showCreateLotteryForm = false;
          await this.fetchCurrentLottery();
        } else { this.showToast(res?.msg || '发起抽奖失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '发起抽奖失败', 'error'); }
    },
    async handleJoinLottery() {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      if (!this.currentLottery) return;
      try {
        const res = await joinLottery(this.currentLottery.id);
        if (res && res.code === 1) {
          this.showToast('参与成功');
          await this.fetchCurrentLottery();
        } else { this.showToast(res?.msg || '参与失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '参与失败', 'error'); }
    },
    async handleDrawLottery() {
      if (!this.currentLottery) return;
      try {
        const res = await drawLottery(this.currentLottery.id);
        if (res && res.code === 1) {
          this.showToast('已开奖');
          await this.fetchCurrentLottery();
        } else { this.showToast(res?.msg || '开奖失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '开奖失败', 'error'); }
    },
    async fetchPkInfo() {
      try {
        const res = await getPkInfo({ room_id: this.$route.params.id });
        if (res && res.code === 1) { this.currentPk = res.data || null; }
      } catch (error) { console.warn('获取PK信息失败:', error?.message); }
    },
    async handleStartPk() {
      if (!this.pkForm.targetRoomId) { this.showToast('请输入对方房间号', 'error'); return; }
      try {
        const res = await startPk({
          room_id: this.$route.params.id,
          target_room_id: this.pkForm.targetRoomId,
          duration: Number(this.pkForm.duration) || 300
        });
        if (res && res.code === 1) {
          this.showToast('PK邀请已发送');
          this.pkForm = { targetRoomId: '', duration: 300 };
          await this.fetchPkInfo();
        } else { this.showToast(res?.msg || '发起PK失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '发起PK失败', 'error'); }
    },
    async handleAcceptPk() {
      if (!this.currentPk) return;
      try {
        const res = await acceptPk(this.currentPk.id);
        if (res && res.code === 1) { this.showToast('已接受PK'); await this.fetchPkInfo(); }
        else { this.showToast(res?.msg || '操作失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '操作失败', 'error'); }
    },
    async handleRejectPk() {
      if (!this.currentPk) return;
      try {
        const res = await rejectPk(this.currentPk.id);
        if (res && res.code === 1) { this.showToast('已拒绝PK'); this.currentPk = null; await this.fetchPkInfo(); }
        else { this.showToast(res?.msg || '操作失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '操作失败', 'error'); }
    },
    async handleEndPk() {
      if (!this.currentPk) return;
      try {
        const res = await endPk(this.currentPk.id);
        if (res && res.code === 1) { this.showToast('PK已结束'); this.currentPk = null; await this.fetchPkInfo(); }
        else { this.showToast(res?.msg || '操作失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '操作失败', 'error'); }
    },
    async fetchMicList() {
      try {
        const res = await getMicList(this.$route.params.id);
        if (res && res.code === 1) { this.micList = res.data?.list || res.data || []; }
      } catch (error) { console.warn('获取连麦列表失败:', error?.message); }
    },
    async handleStartMic() {
      if (!this.micForm.targetUserId) { this.showToast('请输入对方用户ID', 'error'); return; }
      try {
        const res = await startMic({
          room_id: this.$route.params.id,
          target_user_id: Number(this.micForm.targetUserId)
        });
        if (res && res.code === 1) {
          this.showToast('连麦邀请已发送');
          this.micForm = { targetUserId: '' };
          await this.fetchMicList();
        } else { this.showToast(res?.msg || '邀请连麦失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '邀请连麦失败', 'error'); }
    },
    async handleEndMic(micId) {
      try {
        const res = await endMic(micId);
        if (res && res.code === 1) { this.showToast('连麦已结束'); await this.fetchMicList(); }
        else { this.showToast(res?.msg || '操作失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '操作失败', 'error'); }
    },
    fetchLiveInteractData() {
      const roomId = Number(this.videoDetail?.live_room_id || this.videoDetail?.room_id || 0);
      if (!roomId) return;
      this.fetchRankList();
      this.fetchPacketList();
      this.fetchCurrentLottery();
      if (this.isAnchor) { this.fetchPkInfo(); this.fetchMicList(); }
    },

    // ===== 工具栏方法 =====
    openToolbarPanel(name) {
      this.toolbarPanel = name;
    },
    closeToolbarPanel() {
      this.toolbarPanel = '';
    },
    openToolbarModal(name) {
      this.toolbarPanel = '';
      if (name === 'recharge') this.fetchRechargePackagesList();
      if (name === 'bag') this.fetchBagItems();
      if (name === 'treasure') this.fetchTreasureInfo();
      if (name === 'shop') { this.shopTab = 'items'; this.fetchShopItems(); this.fetchBagItems(); }
      this.showToolbarModal = name;
    },
    closeToolbarModal() {
      this.showToolbarModal = '';
    },
    openNobleModal() {
      this.toolbarPanel = '';
      this.fetchNobleInfo();
      this.showNobleModal = true;
    },
    closeNobleModal() {
      this.showNobleModal = false;
    },
    openGuardModal() {
      this.toolbarPanel = '';
      this.fetchGuardInfo();
      this.showGuardModal = true;
    },
    closeGuardModal() {
      this.showGuardModal = false;
    },
    openInteractPanel(name) {
      this.showRankPanel = name === 'rank';
      this.showRedPacketPanel = name === 'redpacket';
      this.showLotteryPanel = name === 'lottery';
      this.showPkPanel = name === 'pk';
      if (name === 'rank') this.fetchRankList();
      if (name === 'redpacket') this.fetchPacketList();
      if (name === 'lottery') this.fetchCurrentLottery();
      if (name === 'pk') { this.fetchPkInfo(); this.fetchMicList(); }
    },
    async sendQuickGift(gift) {
      if (!gift) return;
      if (!this.isLoggedIn) { this.showToast('请先登录后再送礼', 'error'); this.$router.push('/login'); return; }
      if (this.isRewarding) return;
      const cost = Number(gift.price || 0) * Number(this.quickGiftCount || 1);
      const ok = await this.confirmDialog({
        title: '确认送礼',
        message: `确定送出【${gift.name}】x${this.quickGiftCount}，消耗 ${cost} 金币？`
      });
      if (!ok) return;
      try {
        this.isRewarding = true;
        const response = await rewardGift({ video_id: this.$route.params.id, gift_id: gift.id, gift_count: this.quickGiftCount });
        if (response.code === 1) {
          this.showToast('打赏成功');
          this.pushGiftBanner({ nickname: '我', gift_name: gift.name, gift_count: this.quickGiftCount, gift_cover: gift.displayImage });
          this.pullChat();
          if (gift.effectUrl) {
            await this.playGiftEffect(gift.effectUrl);
          }
        } else {
          this.showToast(response.msg || '打赏失败', 'error');
        }
      } catch (error) {
        this.showToast((error && error.message) || '打赏失败，请稍后重试', 'error');
      } finally {
        this.isRewarding = false;
      }
    },
    async handleHalfPriceDraw() {
      if (!this.giftList.length) { this.showToast('暂无礼物可抽取', 'error'); return; }
      if (!this.videoDetail?.id) { this.showToast('直播间信息缺失', 'error'); return; }
      if (this.isRewarding) return;

      const randomGift = this.giftList[Math.floor(Math.random() * this.giftList.length)];
      const cost = Math.max(1, Math.floor((randomGift.price || 1) / 2));
      const ok = await this.confirmDialog({
        title: '半价抽取',
        message: `将随机送出【${randomGift.name}】，消耗 ${cost} 金币，确认吗？`
      });
      if (!ok) return;

      this.isRewarding = true;
      try {
        const res = await rewardGift({
          video_id: this.videoDetail.id,
          gift_id: randomGift.id,
          gift_count: 1,
          is_half: 1
        });
        if (res && res.code === 1) {
          this.showToast(`半价送出 ${randomGift.name}，消耗 ${cost} 金币`, 'success');
          this.pushGiftBanner({ nickname: '我', gift_name: randomGift.name, gift_count: 1, gift_cover: randomGift.displayImage });
          this.pullChat();
          if (randomGift.effectUrl) this.playGiftEffect(randomGift.effectUrl);
        } else {
          this.showToast(res?.msg || '半价抽取失败', 'error');
        }
      } catch (error) {
        this.showToast(error?.message || '半价抽取失败，请稍后重试', 'error');
      } finally {
        this.isRewarding = false;
      }
    },
    // 基础金币按 1 元 = 10 金币计算。
    // 优先用接口的 coins，但若缺失或与 price*10 不一致，则以 price*10 为准，
    // 保证前端展示的比例始终是 1:10（后台数据录错也不会显示出错误比例）。
    pkgBaseCoins(pkg) {
      const price = Number(pkg.price) || 0;
      const expected = Math.round(price * 10);
      const coins = Number(pkg.coins);
      return (coins > 0 && coins === expected) ? coins : expected;
    },
    async fetchRechargePackagesList() {
      try {
        const res = await fetchRechargePackages();
        if (res && res.code === 1) { this.rechargePackagesList = res.data?.list || res.data || []; }
      } catch (error) { console.warn('获取充值套餐失败:', error?.message); }
    },
    createRechargeOrder(pkg) {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      // 真实支付未对接：直接弹出收款二维码，扫码后人工确认到账
      this.rechargeQR = {
        show: true,
        amount: pkg.price,
        url: erweimaImg
      };
    },
    async fetchBagItems() {
      try {
        const res = await getMyItems();
        if (res && res.code === 1) { this.bagItems = res.data?.list || res.data || []; }
      } catch (error) { console.warn('获取背包失败:', error?.message); }
    },
    async useBagItem(item) {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      if (item.using) return;
      const itemName = item.name || item.gift?.name || '礼物';
      const ok = await this.confirmDialog({
        title: '送出背包礼物',
        message: `确定将背包中的【${itemName}】送出到当前直播间？`
      });
      if (!ok) return;
      item.using = true;
      try {
        const res = await useItem(item.id, Number(this.$route.params.id));
        if (res && res.code === 1) {
          const data = res.data || {};
          if (data.is_gift) {
            // 背包礼物送出：提示 + 飘屏横幅 + 特效，与直接打赏一致
            const giftCount = Number(data.quantity) || 1;
            this.showToast(`送出 ${data.gift_name || '礼物'} x${giftCount}`);
            this.pushGiftBanner({ nickname: '我', gift_name: data.gift_name, gift_count: giftCount, gift_cover: data.cover });
            if (data.effect_url) {
              await this.playGiftEffect(data.effect_url);
            }
            this.pullChat();
          } else {
            this.showToast('使用成功');
          }
          await this.fetchBagItems();
        } else { this.showToast(res?.msg || '使用失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '使用失败', 'error'); }
      finally { item.using = false; }
    },
    async fetchNobleInfo() {
      try {
        const levelsRes = await getNobleLevels();
        if (levelsRes && levelsRes.code === 1) { this.nobleLevels = levelsRes.data?.list || levelsRes.data || []; }
      } catch (error) { console.warn('获取贵族等级失败:', error?.message); }
      try {
        const myRes = await getMyNoble();
        if (myRes && myRes.code === 1) {
          this.myNoble = myRes.data?.current_noble || null;
          if (!this.nobleLevels.length && myRes.data?.levels) {
            this.nobleLevels = myRes.data.levels;
          }
        }
      } catch (error) { /* ignore */ }
    },
    async buyNobleLevel(noble) {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      try {
        const res = await buyNoble(noble.id);
        if (res && res.code === 1) {
          const gifts = res.data?.gifts || [];
          if (gifts.length) {
            this.showToast(`开通贵族成功，赠送：${gifts.map(g => `${g.name}×${g.quantity}`).join('、')}（已放入背包）`);
            await this.fetchBagItems();
          } else {
            this.showToast('开通贵族成功');
          }
          await this.fetchNobleInfo();
        } else { this.showToast(res?.msg || '开通失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '开通失败', 'error'); }
    },
    async fetchGuardInfo() {
      const anchorId = this.videoDetail?.user_id;
      try {
        const listRes = await getGuardList(anchorId);
        if (listRes && listRes.code === 1) {
          this.guardList = listRes.data?.list || listRes.data || [];
          this.myGuard = this.guardList.find(g => g.owned) || null;
        }
      } catch (error) { console.warn('获取守护列表失败:', error?.message); }
      // 检查是否已加入当前直播间粉丝团
      this.checkFanClubJoined();
    },
    async checkFanClubJoined() {
      if (!this.isLoggedIn || !this.$route.params.id) return;
      try {
        const res = await getMyBadges();
        if (res && res.code === 1) {
          const roomId = parseInt(this.$route.params.id, 10);
          const list = res.data || [];
          this.fanClubJoined = list.some(b => parseInt(b.room_id, 10) === roomId);
        }
      } catch (e) { /* ignore */ }
    },
    async handleJoinFanClub() {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      if (this.fanClubJoined) { this.showToast('已加入该主播粉丝团'); return; }
      const roomId = parseInt(this.$route.params.id, 10);
      if (!roomId) { this.showToast('直播间ID无效', 'error'); return; }
      const ok = await this.confirmDialog({
        title: '加入粉丝团',
        message: '加入粉丝团需消耗 10 金币，确认加入？'
      });
      if (!ok) return;
      try {
        const res = await joinFanClub(roomId);
        if (res && res.code === 1) {
          this.showToast('加入粉丝团成功');
          this.fanClubJoined = true;
        } else {
          this.showToast(res?.msg || res?.data?.msg || '加入失败', 'error');
        }
      } catch (error) {
        this.showToast(error?.message || '加入失败', 'error');
      }
    },
    async buyGuardLevel(guard) {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      const anchorId = this.videoDetail?.user_id;
      if (!anchorId) { this.showToast('主播信息缺失', 'error'); return; }
      const ok = await this.confirmDialog({
        title: '开通守护',
        message: `确定为该主播开通【${guard.name}】？需消耗 ${guard.price_num || guard.price} 金币`
      });
      if (!ok) return;
      try {
        const res = await buyGuard(guard.level, anchorId);
        if (res && res.code === 1) {
          const gifts = res.data?.gifts || [];
          if (gifts.length) {
            this.showToast(`开通守护成功，赠送：${gifts.map(g => `${g.name}×${g.quantity}`).join('、')}（已放入背包）`);
            await this.fetchBagItems();
          } else {
            this.showToast(res?.msg || '开通守护成功');
          }
          await this.fetchGuardInfo();
        } else { this.showToast(res?.msg || '开通失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '开通失败', 'error'); }
    },
    async fetchTreasureInfo() {
      try {
        const treasureRes = await getMyTreasures();
        if (treasureRes && treasureRes.code === 1) { this.myTreasures = treasureRes.data?.list || treasureRes.data || []; }
      } catch (error) { console.warn('获取宝箱失败:', error?.message); }
      try {
        const statusRes = await getTreasureStatus();
        if (statusRes && statusRes.code === 1) { this.treasureStatus = statusRes.data || null; }
      } catch (error) { /* ignore */ }
    },
    async openTreasureBox() {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      this.openingTreasure = true;
      try {
        const res = await openTreasure(this.$route.params.id);
        if (res && res.code === 1) {
          this.showToast('宝箱开启成功');
          await this.fetchTreasureInfo();
        } else { this.showToast(res?.msg || '开启失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '开启失败', 'error'); }
      finally { this.openingTreasure = false; }
    },
    async fetchShopItems() {
      try {
        const res = await getItemList({ limit: 100 });
        if (res && res.code === 1) { this.shopItems = res.data?.list || res.data || []; }
      } catch (error) { console.warn('获取商品失败:', error?.message); }
    },
    async buyShopItem(item) {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'error'); this.$router.push('/login'); return; }
      if (item.buying) return;
      item.buying = true;
      try {
        const res = await buyItem(item.id, 1);
        if (res && res.code === 1) {
          this.showToast('购买成功');
          await this.fetchBagItems();
        } else { this.showToast(res?.msg || '购买失败', 'error'); }
      } catch (error) { this.showToast(error?.message || '购买失败', 'error'); }
      finally { item.buying = false; }
    }
  },
  created() {
    this.fetchVideoDetail();
    this.fetchComments();
    this.fetchGiftList();
    this.fetchDanmakuList();
    this.fetchMinorMode();
    this.onMinorModeChanged = () => this.fetchMinorMode();
    window.addEventListener('minor-mode-changed', this.onMinorModeChanged);
    this.$nextTick(() => {
      const video = this.$refs.videoPlayer;
      if (video) {
        // 保存监听器引用，便于卸载时移除
        this.handleVideoLoaded = () => {
          this.isPlaying = true;
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => { /* 自动播放被浏览器阻止，忽略 */ });
          }
          video.volume = this.volume / 100;
          video.muted = false;
          // 记录观看历史
          if (this.isLoggedIn && this.$route.params.id) {
            addWatchHistory(this.$route.params.id, 0).catch(() => {});
          }
        };
        video.addEventListener('loadeddata', this.handleVideoLoaded);
      }
    });
  },
  mounted() {
    // 先拉取历史消息，再登记本次进场并拉取新增消息，避免历史进场特效和本次进场重复触发
    this.pullChat()
      .then(() => this.enterRoom())
      .then(() => this.pullChat())
      .finally(() => { this.fetchAudienceList(); });
    this.fetchNobleInfo();
    this.startPolling();
    // 页面挂载时即记录观看历史（无论视频是否加载成功）
    if (this.isLoggedIn && this.$route.params.id) {
      addWatchHistory(this.$route.params.id, 0).catch(() => {});
    }
    this.handleOutsideClick = (e) => {
      if (this.showDanmakuSetting && !e.target.closest('.dm-setting') && !e.target.closest('.control-btn')) { this.showDanmakuSetting = false; }
      if (!e.target.closest('.interact-panel') && !e.target.closest('.interact-btn')) {
        this.showRankPanel = false;
        this.showRedPacketPanel = false;
        this.showLotteryPanel = false;
        this.showPkPanel = false;
      }
      if (!e.target.closest('.toolbar-panel') && !e.target.closest('.toolbar-btn') && !e.target.closest('.quick-gift')) {
        this.toolbarPanel = '';
      }
    };
    document.addEventListener('click', this.handleOutsideClick);
    this.handleVisibility = () => { if (document.hidden) { this.stopPolling(); } else { this.startPolling(); } };
    document.addEventListener('visibilitychange', this.handleVisibility);
  },
  beforeUnmount() {
    this.destroyGiftEffect();
    this.stopPolling();
    // 清理 video 事件监听器
    if (this.handleVideoLoaded) {
      const video = this.$refs.videoPlayer;
      if (video) { video.removeEventListener('loadeddata', this.handleVideoLoaded); }
      this.handleVideoLoaded = null;
    }
    // 清理未触发的 setTimeout
    if (this.svgaSafetyTimer) { clearTimeout(this.svgaSafetyTimer); this.svgaSafetyTimer = null; }
    if (this.enterEffectTimer) { clearTimeout(this.enterEffectTimer); this.enterEffectTimer = null; }
    if (this.heartTimers && this.heartTimers.length) {
      this.heartTimers.forEach(t => clearTimeout(t));
      this.heartTimers = [];
    }
    if (this.bannerTimers && this.bannerTimers.length) {
      this.bannerTimers.forEach(t => clearTimeout(t));
      this.bannerTimers = [];
    }
    if (this.handleOutsideClick) { document.removeEventListener('click', this.handleOutsideClick); }
    if (this.handleVisibility) { document.removeEventListener('visibilitychange', this.handleVisibility); }
    if (this.onMinorModeChanged) { window.removeEventListener('minor-mode-changed', this.onMinorModeChanged); }
  }
}
</script>

<style scoped>
.live-detail {
  padding: 20px 0;
  background: #f0f2f5;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.container {
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 15px;
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
}

.live-content {
  display: flex;
  gap: 20px;
  min-width: 0;
}

.live-main {
  flex: 1;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  min-width: 0;
  width: 100%;
}

.video-container {
  width: 100%;
  background: #000;
  aspect-ratio: 16 / 9;
  position: relative;
  overflow: hidden;
  min-width: 0;
}

.live-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.live-video-fallback {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

/* ==================== 视频控制栏 ==================== */
.video-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 48px;
  background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%);
  display: flex;
  align-items: center;
  padding: 0 16px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 5;
}

.video-container:hover .video-controls {
  opacity: 1;
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.controls-center {
  margin-left: 16px;
}

.video-time {
  color: rgba(255,255,255,0.8);
  font-size: 12px;
  font-family: monospace;
}

.controls-right {
  margin-left: auto;
}

.control-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.85;
  transition: all 0.2s;
  border-radius: 4px;
}

.control-btn:hover {
  opacity: 1;
  background: rgba(255,255,255,0.12);
}

/* ==================== 画质选择 ==================== */
.quality-wrap {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 30;
}

.quality-btn {
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  min-width: 52px;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 31;
}

.quality-text {
  line-height: 1;
}

.quality-panel {
  position: absolute;
  bottom: 44px;
  right: 0;
  min-width: 160px;
  background: rgba(20, 22, 28, 0.96);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 6px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  z-index: 32;
  pointer-events: auto;
  backdrop-filter: blur(8px);
}

.quality-option {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: none;
  color: rgba(255,255,255,0.75);
  cursor: pointer;
  border-radius: 6px;
  text-align: left;
  gap: 8px;
  transition: all 0.2s;
}

.quality-option:hover {
  background: rgba(255,255,255,0.08);
  color: #fff;
}

.quality-option.active {
  color: #ff6a00;
  font-weight: 600;
}

.quality-opt-label {
  font-size: 13px;
  font-weight: 600;
  min-width: 52px;
}

.quality-opt-desc {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  flex: 1;
}

.quality-option.active .quality-opt-desc {
  color: rgba(255,106,0,0.6);
}

.quality-lock {
  display: inline-flex;
  color: rgba(255,255,255,0.4);
}

.volume-slider {
  width: 72px;
  display: flex;
  align-items: center;
}

.volume-slider input {
  width: 100%;
  height: 4px;
  -webkit-appearance: none;
  background: rgba(255,255,255,0.25);
  border-radius: 2px;
  outline: none;
  transition: all 0.2s;
}

.volume-slider input:hover {
  height: 6px;
}

.volume-slider input::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.volume-slider input:hover::-webkit-slider-thumb {
  background: #ff9600;
  width: 14px;
  height: 14px;
}

/* ==================== 直播状态条 ==================== */
.live-topbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 48px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 14px;
  background: linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%);
  z-index: 6;
  pointer-events: none;
}

.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #ff2d55;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 10px;
}

.live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
  animation: live-blink 1.2s infinite;
}

@keyframes live-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
}

.live-stat {
  color: rgba(255,255,255,0.92);
  font-size: 13px;
  text-shadow: 0 1px 3px rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  gap: 4px;
}

.danmaku-count {
  margin-left: auto;
}

/* ==================== 弹幕开关 ==================== */
.dm-toggle.off {
  opacity: 0.45;
}

/* ==================== 弹幕设置面板 ==================== */
.dm-setting {
  position: absolute;
  right: 14px;
  bottom: 56px;
  width: 260px;
  background: rgba(20,20,24,0.94);
  border-radius: 12px;
  padding: 16px;
  z-index: 20;
  box-shadow: 0 8px 28px rgba(0,0,0,0.45);
  backdrop-filter: blur(6px);
}

.dm-setting-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.dm-setting-row:last-child { margin-bottom: 0; }

.dm-setting-label {
  width: 60px;
  flex-shrink: 0;
  color: rgba(255,255,255,0.75);
  font-size: 12px;
}

.dm-setting-row input[type="range"] {
  flex: 1;
  height: 4px;
  -webkit-appearance: none;
  background: rgba(255,255,255,0.25);
  border-radius: 2px;
  outline: none;
}

.dm-setting-row input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff9600;
  cursor: pointer;
}

.dm-setting-value {
  width: 36px;
  text-align: right;
  color: #ff9600;
  font-size: 12px;
}

.dm-chip {
  padding: 4px 12px;
  background: rgba(255,255,255,0.12);
  border: 1px solid transparent;
  border-radius: 12px;
  color: rgba(255,255,255,0.8);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.dm-chip:hover { background: rgba(255,255,255,0.2); }

.dm-chip.active {
  background: rgba(255,150,0,0.2);
  border-color: #ff9600;
  color: #ff9600;
}

/* ==================== 虎牙风格底部工具栏 ==================== */
.live-toolbar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 8px 10px;
  background: linear-gradient(to top, rgba(18,18,22,0.98) 0%, rgba(28,28,34,0.95) 100%);
  border-top: 1px solid rgba(255,255,255,0.06);
  flex-wrap: wrap;
  min-height: 56px;
  z-index: 42;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 10px;
  background: rgba(255,255,255,0.08);
  border: 1px solid transparent;
  border-radius: 16px;
  color: rgba(255,255,255,0.9);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.toolbar-btn:hover {
  background: rgba(255,150,0,0.2);
  border-color: rgba(255,150,0,0.5);
  color: #ffb84d;
}

.toolbar-btn.tool-icon {
  flex-direction: column;
  padding: 4px 8px;
  border-radius: 10px;
  gap: 2px;
  font-size: 10px;
}

.toolbar-btn.tool-icon svg { opacity: 0.9; }

.toolbar-btn.pill-btn {
  padding: 5px 10px;
}

.toolbar-btn.action-btn.recharge {
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  font-weight: 600;
}

.toolbar-btn.action-btn.recharge:hover {
  background: linear-gradient(135deg, #ff7d00, #ffa200);
  box-shadow: 0 2px 10px rgba(255,150,0,0.35);
}

.toolbar-btn.action-btn.noble {
  background: linear-gradient(135deg, #8b5cf6, #a78bfa);
  color: #fff;
}

.toolbar-btn.action-btn.noble:hover {
  background: linear-gradient(135deg, #7c3aed, #9d7cfa);
}

.quick-gifts {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: 4px;
  max-width: 230px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.quick-gifts::-webkit-scrollbar { display: none; }

.quick-gift {
  width: 34px;
  height: 34px;
  padding: 3px;
  background: rgba(255,255,255,0.1);
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quick-gift img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 5px;
}

.quick-gift:hover {
  background: rgba(255,150,0,0.2);
  border-color: rgba(255,150,0,0.5);
  transform: translateY(-2px);
}

.quick-gift.expand-gift {
  color: rgba(255,255,255,0.65);
  background: rgba(255,255,255,0.06);
}

.quick-gift.expand-gift:hover {
  color: #ffb84d;
  background: rgba(255,150,0,0.15);
}

/* 弹幕输入区 */
.toolbar-dm {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px 0 8px;
  flex-shrink: 0;
}

.dm-color-picker { display: flex; gap: 5px; }

.dm-color {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid rgba(255,255,255,0.35);
  transition: all 0.2s;
}

.dm-color:hover { transform: scale(1.15); }

.dm-color.active {
  border-color: #ff9600;
  box-shadow: 0 0 0 2px rgba(255,150,0,0.35);
}

.dm-position-picker { display: flex; gap: 5px; }

.dm-chip.small {
  padding: 3px 10px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  color: rgba(255,255,255,0.85);
  font-size: 12px;
  border-radius: 11px;
}

.dm-chip.small:hover { border-color: #ff9600; color: #ff9600; background: rgba(255,150,0,0.15); }

.dm-chip.small.active {
  background: rgba(255,150,0,0.2);
  border-color: #ff9600;
  color: #ff9600;
}

.dm-input {
  flex: 1;
  min-width: 140px;
  width: 160px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 18px;
  font-size: 14px;
  outline: none;
  background: rgba(255,255,255,0.1);
  color: #fff;
  transition: all 0.2s;
}

.dm-input::placeholder { color: rgba(255,255,255,0.5); }

.dm-input:focus {
  border-color: #ff9600;
  box-shadow: 0 0 0 2px rgba(255,150,0,0.2);
  background: rgba(255,255,255,0.12);
}

.dm-send-btn {
  height: 36px;
  padding: 0 22px;
  background: linear-gradient(145deg, #ff9600, #ff7d00);
  color: #fff;
  border: none;
  border-radius: 18px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;
}

.dm-send-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,150,0,0.35);
}

.dm-send-btn:disabled { background: rgba(255,255,255,0.2); cursor: not-allowed; }

.dm-send-btn.large {
  height: 40px;
  padding: 0 28px;
  border-radius: 20px;
  font-size: 15px;
  background: linear-gradient(145deg, #ffae00, #ff7d00);
  box-shadow: 0 2px 8px rgba(255,150,0,0.3);
}

.dm-send-btn.large:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(255,150,0,0.45);
}

.dm-like-btn {
  height: 36px;
  padding: 0 14px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,77,109,0.4);
  border-radius: 18px;
  color: #ff4d6d;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}

.dm-like-btn:hover {
  background: rgba(255,77,109,0.15);
  transform: scale(1.05);
}

/* ==================== 礼物飘屏 ==================== */
.gift-banner-area {
  position: absolute;
  left: 14px;
  top: 56px;
  z-index: 8;
  pointer-events: none;
}

.gift-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 5px 14px 5px 6px;
  background: linear-gradient(90deg, rgba(255,77,109,0.92), rgba(255,150,0,0.85));
  border-radius: 18px;
  color: #fff;
  font-size: 13px;
  box-shadow: 0 3px 12px rgba(0,0,0,0.3);
}

.gift-banner-img {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(255,255,255,0.25);
}

.gift-banner-user {
  font-weight: 700;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gift-banner-count {
  font-weight: 800;
  font-size: 15px;
  color: #ffe66d;
}

.gift-banner-enter-active,
.gift-banner-leave-active { transition: all 0.4s ease; }

.gift-banner-enter-from { opacity: 0; transform: translateX(-40px); }

.gift-banner-leave-to { opacity: 0; transform: translateX(-20px); }

/* ==================== 双击飘心 ==================== */
.heart-area {
  position: absolute;
  top: 48px;
  left: 0;
  right: 0;
  bottom: 48px;
  z-index: 7;
  overflow: hidden;
}

.heart {
  position: absolute;
  bottom: 10px;
  font-size: 26px;
  animation: heart-float 2.2s ease-out forwards;
  pointer-events: none;
  text-shadow: 0 2px 6px rgba(0,0,0,0.3);
}

@keyframes heart-float {
  0% { opacity: 0; transform: translateY(0) scale(0.4); }
  15% { opacity: 1; transform: translateY(-30px) scale(1.15); }
  100% { opacity: 0; transform: translateY(-260px) translateX(var(--drift, 0)) scale(0.7); }
}

.gift-effect-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 100;
}

.gift-effect-png {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 220px;
  height: 220px;
  object-fit: contain;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 101;
  animation-name: gift-png-pop;
  animation-timing-function: cubic-bezier(0.18, 0.89, 0.32, 1.28);
  animation-fill-mode: forwards;
}

@keyframes gift-png-pop {
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.2) rotate(-15deg); }
  25%  { opacity: 1; transform: translate(-50%, -50%) scale(1.2) rotate(5deg); }
  55%  { opacity: 1; transform: translate(-50%, -50%) scale(1.0) rotate(0deg); }
  80%  { opacity: 1; transform: translate(-50%, -50%) scale(1.05) rotate(0deg); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.4) rotate(0deg); }
}

/* ==================== 进场特效（虎牙风格） ==================== */
.enter-effect {
  position: absolute;
  top: 24%;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 102;
  animation: enter-slide-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
             enter-slide-out 0.35s ease-in 2.85s forwards;
}
@keyframes enter-slide-in {
  0%   { opacity: 0; transform: translateX(150%) translateX(-50%) scale(0.5); }
  55%  { opacity: 1; transform: translateX(-50%) scale(1.06); }
  100% { opacity: 1; transform: translateX(-50%) scale(1); }
}
@keyframes enter-slide-out {
  0%   { opacity: 1; transform: translateX(-50%) scale(1); }
  100% { opacity: 0; transform: translateX(-180%) translateX(-50%) scale(0.85); }
}

/* 柔和光晕 */
.enter-aura {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 210px;
  height: 90px;
  transform: translate(-50%, -50%);
  background: radial-gradient(ellipse, rgba(255, 200, 80, 0.28) 0%, transparent 70%);
  filter: blur(6px);
  animation: enter-aura-pulse 1.6s ease-in-out infinite;
}
.enter-effect.is-noble .enter-aura {
  background: radial-gradient(ellipse, rgba(200, 120, 255, 0.3) 0%, transparent 70%);
}
@keyframes enter-aura-pulse {
  0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.08); }
}

/* 横幅主体：半透明深色渐变，干净精致 */
.enter-banner {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 26px 9px 14px;
  border-radius: 34px;
  background: linear-gradient(135deg, rgba(28, 26, 46, 0.82), rgba(56, 44, 20, 0.82));
  border: 1px solid rgba(255, 214, 79, 0.55);
  box-shadow:
    0 0 16px rgba(255, 180, 0, 0.35),
    0 8px 24px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  z-index: 2;
}
.enter-effect.is-noble .enter-banner {
  background: linear-gradient(135deg, rgba(40, 26, 60, 0.82), rgba(70, 30, 90, 0.82));
  border-color: rgba(200, 130, 255, 0.6);
  box-shadow:
    0 0 16px rgba(190, 110, 255, 0.35),
    0 8px 24px rgba(0, 0, 0, 0.45);
}

/* 头像区域 */
.enter-banner-avatar {
  position: relative;
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  z-index: 2;
}
/* 贵族皇冠 */
.enter-crown {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: auto;
  color: #ffd54f;
  filter: drop-shadow(0 1px 4px rgba(255, 200, 60, 0.9));
  animation: enter-crown-float 1.6s ease-in-out infinite;
  z-index: 3;
}
.enter-effect.is-noble .enter-crown {
  color: #e0aaff;
  filter: drop-shadow(0 1px 4px rgba(200, 130, 255, 0.95));
}
@keyframes enter-crown-float {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(-3px); }
}
.enter-avatar-ring {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, #fff5d6, #ffd54f, #ffb300, #ffd54f, #fff5d6);
  animation: enter-ring-rotate 3s linear infinite;
}
.enter-effect.is-noble .enter-avatar-ring {
  background: conic-gradient(from 0deg, #f8d4ff, #d040ff, #a020f0, #d040ff, #f8d4ff);
}
@keyframes enter-ring-rotate {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.enter-banner-avatar-img {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  border: 2px solid #fff;
  z-index: 1;
}

/* 文字区域 */
.enter-banner-text {
  display: flex;
  align-items: baseline;
  gap: 5px;
  white-space: nowrap;
  z-index: 2;
}
.enter-banner-welcome {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}
.enter-banner-name {
  font-size: 17px;
  color: #fff;
  font-weight: 800;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9), 0 0 8px rgba(255, 214, 79, 0.55);
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.enter-effect.is-noble .enter-banner-name {
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9), 0 0 8px rgba(200, 130, 255, 0.6);
}
.enter-banner-come {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}
.enter-banner-noble {
  display: inline-block;
  background: linear-gradient(135deg, #ff4d9d, #a020f0);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 9px;
  white-space: nowrap;
  line-height: 16px;
  margin-left: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4);
  vertical-align: middle;
}
.enter-fan-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: linear-gradient(135deg, #2dd4bf, #0ea5e9);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 9px;
  white-space: nowrap;
  line-height: 15px;
  margin-left: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.35);
  vertical-align: middle;
}
.enter-fan-name {
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
}
.enter-fan-img {
  width: 22px;
  height: 14px;
  display: block;
}
.enter-fan-lv {
  font-size: 10px;
  font-weight: 700;
}

/* ==================== 视频信息 ==================== */
.video-info {
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #f0f0f0;
  gap: 10px;
}

.video-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.video-title {
  font-size: 20px;
  color: #1a1a2e;
  margin: 0;
  font-weight: 700;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-title-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.video-view-count {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  border-radius: 14px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.video-stats {
  font-size: 13px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
}

.video-stats .views,
.video-stats .category {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #94a3b8;
}

.author-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0;
  flex-wrap: wrap;
}

.author-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #f0f0f0;
  transition: border-color 0.3s;
}

.author-avatar:hover {
  border-color: #ff9600;
}

.author-avatar img { width: 100%; height: 100%; object-fit: cover; }

.author-details { flex: 1; min-width: 0; }

.author-name {
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  cursor: pointer;
  transition: color 0.2s;
}
.author-name:hover {
  color: #ff6a00;
}
.author-avatar {
  cursor: pointer;
  transition: transform 0.2s;
}
.author-avatar:hover {
  transform: scale(1.05);
}

.author-id {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

.author-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 12px;
  color: #64748b;
  cursor: default;
}

.meta-item.category {
  color: #ff9600;
  background: #fff7ed;
  border-color: #fed7aa;
}

.meta-item.rank-tag {
  color: #8b5cf6;
  background: #f5f3ff;
  border-color: #ddd6fe;
}

.meta-item.guard-tag {
  color: #ef4444;
  background: #fef2f2;
  border-color: #fecaca;
}

.follow-btn {
  padding: 6px 18px;
  border: 1px solid #ff9600;
  border-radius: 18px;
  background: #fff;
  color: #ff9600;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s;
  outline: none;
  flex-shrink: 0;
}

.follow-btn:hover { background: #fff5e6; transform: translateY(-1px); box-shadow: 0 2px 8px rgba(255,150,0,0.15); }

.follow-btn.followed {
  background: #f5f5f5;
  border-color: #e0e0e0;
  color: #94a3b8;
}

.follow-btn.followed:hover {
  background: #fef2f2;
  border-color: #ef4444;
  color: #ef4444;
}

.report-link {
  margin-left: auto;
  padding: 4px 14px;
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  color: #94a3b8;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.25s;
}

.report-link:hover { border-color: #ef4444; color: #ef4444; background: #fef2f2; }

/* ==================== 聊天区域 ==================== */
.chat-area {
  width: 340px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  height: 700px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

.chat-content-panel {
  order: 2;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.audience-content-panel {
  order: 1;
  flex: 0 0 160px;
  height: 160px;
  max-height: 160px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #edf0f3;
  background: #fffaf2;
}

.audience-content-panel.is-hidden {
  display: none;
}

.live-input-area {
  order: 3;
}

/* 青少年模式：弹幕输入区替换为说明条 */
.live-input-area.minor-chat-off {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 12px;
  background: #f6f7f9;
  border-top: 1px solid #ececec;
  color: #7a8a85;
  font-size: 13px;
}
.live-input-area.minor-chat-off svg {
  flex: 0 0 auto;
  color: #1f7a5a;
}

.chat-tabs {
  order: 0;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  padding: 0 12px;
  flex-shrink: 0;
  background: #fafbfc;
}

.chat-tabs.audience-tabs {
  padding: 0 10px;
  gap: 4px;
}

.chat-tab {
  padding: 12px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
  cursor: pointer;
  position: relative;
  transition: all 0.25s;
  user-select: none;
  white-space: nowrap;
}

.audience-tabs .chat-tab {
  padding: 12px 8px;
  font-size: 12px;
}

.chat-tab:hover { color: #ff9600; }

.chat-tab.active { color: #ff9600; }

.chat-tab.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 8px;
  right: 8px;
  height: 2.5px;
  background: linear-gradient(90deg, #ff9600, #ff7d00);
  border-radius: 2px;
}

.chat-online {
  margin-left: auto;
  font-size: 11px;
  color: #ff4d6d;
  font-weight: 600;
  background: rgba(255,77,109,0.08);
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
}

/* 二级标签：实时互动 / 评论 */
.chat-sub-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.chat-sub-tab {
  padding: 5px 12px;
  font-size: 12px;
  color: #64748b;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.chat-sub-tab:hover { color: #ff9600; border-color: #ff9600; }

.chat-sub-tab.active {
  color: #fff;
  background: linear-gradient(135deg, #ff9600, #ff7d00);
  border-color: #ff9600;
}

/* 观众列表 */
.audience-content-panel .audience-list {
  flex: 0 0 159px;
  height: 159px;
  min-height: 159px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 8px 12px;
  scrollbar-width: thin;
  scrollbar-color: #e0e0e0 transparent;
}

.audience-list::-webkit-scrollbar { width: 4px; }

.audience-list::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 2px;
}

.audience-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px 6px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.audience-item:hover { background: #f1f5f9; }

.audience-item img {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
}

.audience-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
}

.audience-week-reward {
  color: #c56d00;
  font-size: 10px;
  line-height: 1.2;
  font-weight: 700;
  white-space: nowrap;
}

.audience-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  max-width: 48%;
  flex-shrink: 1;
  overflow: hidden;
}

.audience-right .audience-week-reward {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audience-info-line {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.audience-name {
  font-size: 13px;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audience-tag {
  font-size: 10px;
  color: #ff9600;
  background: rgba(255,150,0,0.1);
  padding: 1px 5px;
  border-radius: 4px;
  width: fit-content;
}

.audience-level {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  padding: 2px 6px;
  border-radius: 6px;
  background: #94a3b8;
}

.audience-level.level-1, .audience-level.level-2 { background: #94a3b8; }
.audience-level.level-3, .audience-level.level-4, .audience-level.level-5 { background: #22c55e; }
.audience-level.level-6, .audience-level.level-7, .audience-level.level-8 { background: #3b82f6; }
.audience-level.level-9, .audience-level.level-10, .audience-level.level-11 { background: #a855f7; }
.audience-level.level-12, .audience-level.level-13, .audience-level.level-14 { background: #f97316; }
.audience-level.level-15, .audience-level.level-16, .audience-level.level-17 { background: #ef4444; }
.audience-level.level-18, .audience-level.level-19, .audience-level.level-20 { background: #eab308; }

/* 贵族徽章（贵宾列表） */
.audience-item.is-noble {
  background: linear-gradient(90deg, rgba(255, 196, 0, 0.08), rgba(255, 122, 24, 0.05));
  border: 1px solid rgba(255, 176, 32, 0.35);
}
.audience-item.is-noble:hover { background: linear-gradient(90deg, rgba(255, 196, 0, 0.15), rgba(255, 122, 24, 0.09)); }
.audience-item .audience-noble-icon {
  width: 26px;
  height: 26px;
  min-width: 26px;
  min-height: 26px;
  object-fit: contain;
  flex-shrink: 0;
  vertical-align: middle;
  border-radius: 4px;
}

/* 粉丝牌等级徽章（虎牙素材：1-20 png，21-50 webp） */
.audience-item .audience-fan-badge {
  height: 20px;
  width: auto;
  min-width: 0;
  max-width: 90px;
  border-radius: 0;
  object-fit: contain;
  flex-shrink: 0;
  vertical-align: middle;
}

/* 贵宾列表：粉丝牌名称叠在徽章图片上 */
.audience-fan-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 90px;
}
.audience-fan-name {
  position: absolute;
  left: 34%;
  right: 3px;
  top: 50%;
  bottom: auto;
  transform: translateY(-50%);
  z-index: 1;
  max-width: none;
  font-size: 10px;
  line-height: 1.2;
  text-align: center;
  color: #fff;
  background: transparent;
  padding: 0;
  border-radius: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}
.audience-fan-lv {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  padding: 2px 6px;
  border-radius: 6px;
  background: #f97316;
}

/* 聊天区：粉丝牌名称叠在徽章图片上 */
.fan-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-right: 4px;
  flex-shrink: 0;
}
.fan-badge-name {
  position: absolute;
  left: 34%;
  right: 3px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  max-width: none;
  font-size: 10px;
  line-height: 1.2;
  text-align: center;
  color: #fff;
  background: transparent;
  padding: 0;
  border-radius: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}
.chat-fan-badge {
  height: 22px;
  width: auto;
  border-radius: 0;
  object-fit: contain;
  vertical-align: middle;
}
.fan-badge-lv {
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #f97316;
  padding: 0 4px;
  border-radius: 3px;
  line-height: 16px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px;
  scrollbar-width: thin;
  scrollbar-color: #e0e0e0 transparent;
}

.chat-messages::-webkit-scrollbar {
  width: 4px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 2px;
}

.message-item {
  margin-bottom: 12px;
  line-height: 1.5;
  font-size: 14px;
  word-break: break-word;
}

.user-name { font-weight: 600; margin-right: 5px; color: #1a1a2e; }

.message-content { color: #475569; }

.system-message { color: rgb(51, 204, 0); }

/* ==================== 评论输入框 ==================== */
.chat-input-area {
  padding: 12px 14px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
  background: #fafbfc;
  flex-shrink: 0;
}

.input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
}

textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  transition: all 0.3s ease;
  background: #fff;
  box-sizing: border-box;
  color: #1e293b;
}

textarea:focus {
  border-color: #ff9600;
  box-shadow: 0 0 0 3px rgba(255,150,0,0.08);
}

textarea::placeholder {
  color: #94a3b8;
}

.input-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  padding: 0 2px;
}

.input-tip { font-size: 11px; color: #94a3b8; }

.input-limit {
  font-size: 11px;
  color: #94a3b8;
  transition: color 0.3s;
}

.limit-warning { color: #ff9600; font-weight: 500; }

.send-btn {
  padding: 0 16px;
  height: 80px;
  background: linear-gradient(145deg, #ff9600, #ff7d00);
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(255,150,0,0.2);
}

.send-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(255,150,0,0.3);
}

.send-btn:disabled { background: #e2e8f0; box-shadow: none; cursor: not-allowed; }

.send-text { font-size: 13px; font-weight: 600; }

/* ==================== 实时互动消息流 ==================== */
.live-stream { 
  background: #f8f9fa; 
  display: flex;
  flex-direction: column;
  gap: 0;
}

.live-msg {
  margin-bottom: 6px;
  padding: 7px 10px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}

.msg-nick { color: #4a7fc1; font-weight: 600; margin-right: 2px; }

.msg-text { color: #475569; }

.live-msg-enter { background: linear-gradient(90deg, #f0f7ff, #fff); border-left: 2px solid #4a7fc1; }

.enter-nick { color: #2c7be5; }

.enter-text { color: #94a3b8; font-size: 12px; }

.live-msg-gift { background: linear-gradient(90deg, #fff4f6, #fff); border-left: 3px solid #ff4d6d; }

.gift-nick { color: #ff4d6d; }

.gift-text { color: #94a3b8; font-size: 12px; }

.msg-gift-img {
  width: 20px;
  height: 20px;
  vertical-align: middle;
  margin: 0 3px;
  border-radius: 4px;
  object-fit: cover;
}

.gift-name-text { color: #ff7d00; font-weight: 600; }
.gift-count-text { color: #ff2d55; font-weight: 800; margin-left: 3px; }

.live-msg-like { background: linear-gradient(90deg, #fff0f3, #fff); border-left: 2px solid #ff85a1; }

.like-nick { color: #ff4d6d; }

.like-text { color: #ff85a1; font-size: 12px; }

.live-msg-system .sys-text { color: #16a34a; font-size: 12px; }

.badge-noble {
  display: inline-block;
  background: linear-gradient(135deg, #ffb400, #ff6b00);
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  padding: 0 6px;
  border-radius: 3px;
  margin-right: 4px;
  vertical-align: middle;
  font-weight: 700;
}

.chat-noble-icon {
  height: 24px;
  width: auto;
  object-fit: contain;
  vertical-align: middle;
  margin-right: 5px;
  flex-shrink: 0;
  border-radius: 3px;
}

.enter-banner-noble-icon {
  height: 26px;
  width: auto;
  object-fit: contain;
  vertical-align: middle;
  margin-left: 6px;
}

.badge-level {
  display: inline-block;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  padding: 0 6px;
  border-radius: 3px;
  margin-right: 4px;
  vertical-align: middle;
  font-weight: 700;
}

.live-empty { text-align: center; color: #94a3b8; font-size: 13px; padding: 40px 0; }

.live-input-area {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #f0f0f0;
  background: #fafbfc;
  flex-shrink: 0;
}

.live-input-row {
  display: flex;
  gap: 8px;
}

/* 右侧发送区弹幕选项（浅色主题） */
.chat-dm-options {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.chat-dm-options .dm-color {
  width: 16px;
  height: 16px;
  border-width: 1px;
  border-color: rgba(0, 0, 0, 0.18);
}

.chat-dm-options .dm-color.active {
  border-color: #ff9600;
  box-shadow: 0 0 0 2px rgba(255, 150, 0, 0.3);
}

.chat-dm-chip {
  padding: 2px 10px;
  font-size: 12px;
  border-radius: 11px;
  border: 1px solid #dbe0e6;
  background: #f3f4f6;
  color: #5b6470;
  cursor: pointer;
  transition: all 0.2s;
}

.chat-dm-chip:hover { border-color: #ff9600; color: #ff9600; }

.chat-dm-chip.active {
  border-color: #ff9600;
  color: #ff9600;
  background: rgba(255, 150, 0, 0.12);
}

.live-input {
  flex: 1;
  height: 38px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  border-radius: 19px;
  font-size: 14px;
  outline: none;
  background: #fff;
  transition: all 0.2s;
  color: #1e293b;
}

.live-input:focus { border-color: #ff9600; box-shadow: 0 0 0 3px rgba(255,150,0,0.08); }

.live-input::placeholder { color: #94a3b8; }

.live-send-btn {
  height: 38px;
  padding: 0 20px;
  background: linear-gradient(145deg, #ff9600, #ff7d00);
  color: #fff;
  border: none;
  border-radius: 19px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(255,150,0,0.2);
}

.live-send-btn:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,150,0,0.3); }

.live-send-btn:disabled { background: #e2e8f0; box-shadow: none; cursor: not-allowed; }

/* ==================== Toast ==================== */
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  pointer-events: none;
}

.toast-message {
  padding: 10px 20px;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  text-align: center;
  animation: fadeInOut 2s ease;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  gap: 8px;
}

.toast-message.success { background: rgba(22,163,74,0.95); }
.toast-message.error { background: rgba(220,53,69,0.95); }

@keyframes fadeInOut {
  0% { opacity: 0; transform: translateY(-20px); }
  10% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-20px); }
}

/* ==================== 举报弹窗 ==================== */
.report-modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.report-modal {
  width: 420px;
  max-width: 92vw;
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
}

.report-modal-title {
  font-size: 18px;
  color: #1a1a2e;
  margin: 0 0 16px;
  text-align: center;
  font-weight: 700;
}

.report-reasons { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 16px; }

.report-reason-btn {
  padding: 8px 4px;
  background: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 8px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
}

.report-reason-btn:hover { border-color: #ff9600; color: #ff9600; }

.report-reason-btn.active { background: #ff9600; border-color: #ff9600; color: #fff; }

.report-textarea, .report-contact {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 12px;
  transition: all 0.3s;
}

.report-textarea:focus, .report-contact:focus { border-color: #ff9600; background: #fff; }

.report-textarea { resize: vertical; }

.report-actions { display: flex; gap: 12px; margin-top: 4px; }

.report-cancel, .report-submit {
  flex: 1;
  height: 40px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.3s;
}

.report-cancel { background: #f1f5f9; color: #475569; }
.report-cancel:hover { background: #e2e8f0; }

.report-submit { background: #ff9600; color: #fff; }
.report-submit:not(:disabled):hover { background: #ff7d00; transform: translateY(-1px); }
.report-submit:disabled { background: #e0e0e0; cursor: not-allowed; }

/* ==================== 消息用户 ==================== */
.message-user { display: flex; align-items: center; margin-bottom: 5px; }

.user-avatar { width: 32px; height: 32px; border-radius: 50%; margin-right: 8px; }

.message-body { margin-left: 40px; }

.message-info { display: flex; gap: 15px; margin-top: 5px; font-size: 12px; color: #999; }

.loading-more, .no-more { text-align: center; padding: 10px; color: #999; font-size: 14px; }

.system-message { background: rgba(51, 204, 0, 0.08); padding: 10px; border-radius: 6px; margin-bottom: 15px; }

/* ==================== 互动浮动面板通用 ==================== */
.interact-panel {
  position: absolute;
  right: 14px;
  top: auto;
  bottom: 110px;
  width: 280px;
  max-height: 360px;
  background: rgba(20, 20, 24, 0.95);
  border-radius: 12px;
  z-index: 30;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  overflow: hidden;
  animation: panel-slide-in 0.25s ease;
}

@keyframes panel-slide-in {
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
}

.panel-header-sm {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.panel-close {
  background: rgba(255, 255, 255, 0.22);
  border: none;
  color: #fff;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.panel-close:hover { background: rgba(255, 255, 255, 0.4); transform: rotate(90deg); }

.empty-text-sm {
  text-align: center;
  color: rgba(255, 255, 255, 0.45);
  font-size: 12px;
  padding: 30px 0;
}

/* ==================== 排行榜 ==================== */
.rank-period-tabs {
  display: flex;
  gap: 4px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
}

.rank-period-tabs span {
  flex: 1;
  text-align: center;
  padding: 5px 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.rank-period-tabs span:hover { color: #fff; background: rgba(255, 255, 255, 0.08); }

.rank-period-tabs span.active {
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  font-weight: 600;
}

.rank-list {
  flex: 1;
  overflow-y: auto;
  padding: 6px 10px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 150, 0, 0.4) transparent;
}

.rank-list::-webkit-scrollbar { width: 4px; }
.rank-list::-webkit-scrollbar-thumb { background: rgba(255, 150, 0, 0.4); border-radius: 2px; }

.rank-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 6px;
  border-radius: 8px;
  transition: all 0.2s;
}

.rank-item:hover { background: rgba(255, 255, 255, 0.05); }

.rank-item.top { background: rgba(255, 150, 0, 0.08); }

.rank-num {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  flex-shrink: 0;
}

.rank-num.rank-1 { background: linear-gradient(135deg, #ffd700, #ffa500); color: #fff; }
.rank-num.rank-2 { background: linear-gradient(135deg, #d4d4d4, #a8a8a8); color: #fff; }
.rank-num.rank-3 { background: linear-gradient(135deg, #e6a067, #b87333); color: #fff; }

.rank-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.rank-name {
  flex: 1;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.88);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-amount {
  font-size: 13px;
  color: #ff9600;
  font-weight: 600;
  flex-shrink: 0;
}

/* ==================== 红包 ==================== */
.redpacket-panel { bottom: 56px; }

.redpacket-actions {
  padding: 10px 12px;
  flex-shrink: 0;
}

.send-packet-btn {
  width: 100%;
  height: 34px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 17px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(255, 106, 0, 0.3);
}

.send-packet-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255, 106, 0, 0.4); }

.send-packet-form {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 12px 10px;
  flex-shrink: 0;
}

.send-packet-form input,
.send-packet-form select {
  height: 32px;
  padding: 0 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #fff;
  font-size: 12px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
}

.send-packet-form input::placeholder { color: rgba(255, 255, 255, 0.4); }

.send-packet-form input:focus,
.send-packet-form select:focus {
  border-color: #ff9600;
  background: rgba(255, 150, 0, 0.1);
}

.send-packet-form select option { color: #333; }

.btn-send-packet {
  height: 32px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-send-packet:hover { box-shadow: 0 2px 8px rgba(255, 106, 0, 0.4); }

.packet-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 10px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 150, 0, 0.4) transparent;
}

.packet-list::-webkit-scrollbar { width: 4px; }
.packet-list::-webkit-scrollbar-thumb { background: rgba(255, 150, 0, 0.4); border-radius: 2px; }

.packet-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  margin-bottom: 8px;
  background: rgba(255, 106, 0, 0.12);
  border: 1px solid rgba(255, 106, 0, 0.25);
  border-radius: 8px;
  transition: all 0.2s;
}

.packet-item:hover { background: rgba(255, 106, 0, 0.2); }

.packet-item.grabbed { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.12); }

.packet-item.empty { opacity: 0.55; }

.packet-info { flex: 1; min-width: 0; }

.packet-sender {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.packet-detail { font-size: 11px; color: rgba(255, 255, 255, 0.55); margin-top: 2px; }

.grab-btn {
  width: 40px;
  height: 32px;
  background: linear-gradient(135deg, #ff2d55, #ff6a00);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
}

.grab-btn:hover { transform: scale(1.08); box-shadow: 0 2px 8px rgba(255, 45, 85, 0.4); }

.grabbed-text { font-size: 12px; color: #ff9600; font-weight: 600; flex-shrink: 0; }

.empty-text { font-size: 12px; color: rgba(255, 255, 255, 0.4); flex-shrink: 0; }

/* ==================== 抽奖 ==================== */
.lottery-panel { bottom: 56px; }

.anchor-lottery-actions { padding: 10px 12px; flex-shrink: 0; }

.create-lottery-btn {
  width: 100%;
  height: 34px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 17px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(255, 106, 0, 0.3);
}

.create-lottery-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255, 106, 0, 0.4); }

.create-lottery-form {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 12px 10px;
  flex-shrink: 0;
}

.create-lottery-form input,
.create-lottery-form select {
  height: 32px;
  padding: 0 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #fff;
  font-size: 12px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
}

.create-lottery-form input::placeholder { color: rgba(255, 255, 255, 0.4); }

.create-lottery-form input:focus,
.create-lottery-form select:focus {
  border-color: #ff9600;
  background: rgba(255, 150, 0, 0.1);
}

.create-lottery-form select option { color: #333; }

.btn-create-lottery {
  height: 32px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-create-lottery:hover { box-shadow: 0 2px 8px rgba(255, 106, 0, 0.4); }

.current-lottery {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 150, 0, 0.4) transparent;
}

.current-lottery::-webkit-scrollbar { width: 4px; }
.current-lottery::-webkit-scrollbar-thumb { background: rgba(255, 150, 0, 0.4); border-radius: 2px; }

.lottery-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 6px;
}

.lottery-prize {
  font-size: 13px;
  color: #ff9600;
  font-weight: 600;
  margin-bottom: 4px;
}

.lottery-meta { font-size: 12px; color: rgba(255, 255, 255, 0.55); margin-bottom: 10px; }

.lottery-actions { margin: 8px 0; }

.join-lottery-btn,
.draw-lottery-btn {
  width: 100%;
  height: 34px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 17px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(255, 106, 0, 0.3);
}

.join-lottery-btn:not(:disabled):hover,
.draw-lottery-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255, 106, 0, 0.4); }

.join-lottery-btn:disabled { background: rgba(255, 255, 255, 0.12); box-shadow: none; cursor: not-allowed; color: rgba(255, 255, 255, 0.5); }

.lottery-winners {
  margin-top: 10px;
  padding: 10px;
  background: rgba(255, 150, 0, 0.1);
  border: 1px solid rgba(255, 150, 0, 0.25);
  border-radius: 8px;
}

.winners-title { font-size: 12px; font-weight: 600; color: #ff9600; margin-bottom: 6px; }

.winner-item { font-size: 12px; color: rgba(255, 255, 255, 0.8); padding: 3px 0; }

/* ==================== 连麦 / PK ==================== */
.pk-panel { bottom: 56px; }

.pk-tabs {
  display: flex;
  gap: 4px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
}

.pk-tabs span {
  flex: 1;
  text-align: center;
  padding: 5px 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.pk-tabs span:hover { color: #fff; background: rgba(255, 255, 255, 0.08); }

.pk-tabs span.active {
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  font-weight: 600;
}

.pk-create,
.mic-create {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
}

.pk-create input,
.mic-create input {
  height: 32px;
  padding: 0 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #fff;
  font-size: 12px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
}

.pk-create input::placeholder,
.mic-create input::placeholder { color: rgba(255, 255, 255, 0.4); }

.pk-create input:focus,
.mic-create input:focus { border-color: #ff9600; background: rgba(255, 150, 0, 0.1); }

.btn-start-pk,
.btn-start-mic {
  height: 32px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-start-pk:hover,
.btn-start-mic:hover { box-shadow: 0 2px 8px rgba(255, 106, 0, 0.4); }

.pk-info { padding: 12px; }

.pk-progress {
  display: flex;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.pk-side {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  transition: width 0.4s ease;
  overflow: hidden;
  white-space: nowrap;
}

.pk-side.a { background: linear-gradient(135deg, #ff6a00, #ff9600); }

.pk-side.b { background: linear-gradient(135deg, #2d8cff, #4aa3ff); justify-content: flex-end; }

.pk-timer {
  text-align: center;
  font-size: 13px;
  color: #ff9600;
  font-weight: 600;
  margin-bottom: 10px;
}

.btn-accept-pk,
.btn-reject-pk,
.btn-end-pk,
.btn-end-mic {
  width: 100%;
  height: 32px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 6px;
}

.btn-accept-pk { background: linear-gradient(135deg, #ff6a00, #ff9600); color: #fff; }
.btn-accept-pk:hover { box-shadow: 0 2px 8px rgba(255, 106, 0, 0.4); }

.btn-reject-pk { background: rgba(255, 255, 255, 0.1); color: rgba(255, 255, 255, 0.75); }
.btn-reject-pk:hover { background: rgba(255, 77, 109, 0.25); color: #ff4d6d; }

.btn-end-pk { background: rgba(255, 77, 109, 0.85); color: #fff; }
.btn-end-pk:hover { background: #ff2d55; box-shadow: 0 2px 8px rgba(255, 45, 85, 0.4); }

.mic-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 10px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 150, 0, 0.4) transparent;
}

.mic-list::-webkit-scrollbar { width: 4px; }
.mic-list::-webkit-scrollbar-thumb { background: rgba(255, 150, 0, 0.4); border-radius: 2px; }

.mic-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 13px;
  color: #fff;
}

.mic-item span:first-child {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mic-status { font-size: 11px; color: #ff9600; }

.btn-end-mic {
  width: auto;
  padding: 0 12px;
  height: 26px;
  background: rgba(255, 77, 109, 0.85);
  color: #fff;
  margin-top: 0;
  font-size: 12px;
}

.btn-end-mic:hover { background: #ff2d55; }

/* ==================== 工具栏扩展面板 ==================== */
.toolbar-panel {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 72px;
  max-width: 520px;
  margin: 0 auto;
  background: linear-gradient(145deg, rgba(20, 25, 35, 0.98), rgba(11, 15, 24, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 22px;
  z-index: 60;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(255, 173, 79, 0.08);
  overflow: hidden;
  animation: toolbar-panel-in 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.toolbar-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 24px;
  right: 24px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #ffb347, #ffd166, transparent);
}

.toolbar-panel .panel-header-sm {
  padding: 18px 20px 14px;
  background: transparent;
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0;
  box-shadow: none;
}

.toolbar-panel .panel-close {
  width: 28px;
  height: 28px;
  color: rgba(255, 255, 255, 0.75);
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  font-size: 18px;
}

.toolbar-panel .panel-close:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  transform: rotate(90deg) scale(1.05);
}

.toolbar-panel .panel-body {
  padding: 4px 18px 20px;
  max-height: 340px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 179, 71, 0.45) transparent;
}

.more-menu {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.more-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  min-height: 86px;
  padding: 15px 6px 12px;
  color: rgba(255, 255, 255, 0.86);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.035));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.more-item svg {
  width: 26px;
  height: 26px;
  padding: 6px;
  color: #ffd166;
  background: rgba(255, 190, 80, 0.14);
  border-radius: 10px;
  filter: none;
  transition: transform 0.2s ease;
}

.more-item:hover {
  color: #fff;
  background: linear-gradient(145deg, rgba(255, 190, 80, 0.22), rgba(255, 255, 255, 0.07));
  border-color: rgba(255, 209, 102, 0.55);
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(255, 179, 71, 0.12);
}

.more-item:hover svg { transform: scale(1.08); }

.gift-picker {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

/* 弹窗内礼物选择：3 列网格 */
.gift-picker-modal {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.gift-picker-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 12px 4px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.gift-picker-item:hover {
  background: rgba(255,255,255,0.09);
  transform: translateY(-2px);
}

.gift-picker-item.selected {
  border-color: #ff9600;
  background: linear-gradient(135deg, rgba(255,150,0,0.2), rgba(255,150,0,0.06));
  box-shadow: 0 4px 16px rgba(255, 150, 0, 0.2);
}

.gift-picker-item img {
  width: 46px;
  height: 46px;
  object-fit: contain;
  filter: drop-shadow(0 3px 5px rgba(0,0,0,0.25));
  transition: transform 0.2s ease;
}

.gift-picker-item:hover img { transform: scale(1.08); }

.gift-picker-name {
  font-size: 12px;
  color: rgba(255,255,255,0.92);
  font-weight: 500;
}

.gift-picker-price {
  font-size: 11px;
  color: #ff9600;
  font-weight: 600;
}

.recharge-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.recharge-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 16px 6px;
  background: linear-gradient(135deg, rgba(255,150,0,0.16), rgba(255,150,0,0.04));
  border: 1px solid rgba(255,150,0,0.22);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.recharge-item:hover {
  background: linear-gradient(135deg, rgba(255,150,0,0.28), rgba(255,150,0,0.1));
  transform: translateY(-3px);
  box-shadow: 0 6px 18px rgba(255, 150, 0, 0.18);
  border-color: rgba(255,150,0,0.4);
}

.recharge-coin {
  font-size: 20px;
  font-weight: 800;
  color: #ff9600;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.recharge-unit {
  font-size: 11px;
  color: rgba(255,255,255,0.55);
}

.recharge-price {
  font-size: 13px;
  color: #fff;
  font-weight: 600;
}

.recharge-bonus {
  font-size: 10px;
  color: #ffca6a;
  background: rgba(255, 150, 0, 0.16);
  padding: 1px 6px;
  border-radius: 999px;
  line-height: 1.5;
}

.recharge-rate-tip {
  margin-top: 12px;
  text-align: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
}

.bag-panel, .noble-list, .guard-list, .treasure-list, .shop-list, .bag-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bag-item, .noble-item, .guard-item, .treasure-item, .shop-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 12px;
  transition: all 0.2s ease;
}

.bag-item:hover, .noble-item:hover, .guard-item:hover, .treasure-item:hover, .shop-item:hover {
  background: rgba(255,255,255,0.08);
  border-color: rgba(255,150,0,0.2);
  transform: translateX(2px);
}

.bag-item img, .noble-item img, .guard-item img, .treasure-item img, .shop-item img {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 10px;
  box-shadow: 0 3px 8px rgba(0,0,0,0.25);
}

.bag-info, .noble-info, .shop-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bag-name, .noble-name, .guard-name, .treasure-name, .shop-name {
  font-size: 13px;
  color: rgba(255,255,255,0.9);
}

.bag-count, .treasure-count {
  font-size: 12px;
  color: rgba(255,255,255,0.55);
}

.bag-use, .shop-buy, .open-treasure-btn {
  padding: 6px 14px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  border: none;
  border-radius: 14px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 3px 10px rgba(255, 106, 0, 0.25);
}

.bag-use:disabled, .shop-buy:disabled, .open-treasure-btn:disabled {
  background: rgba(255,255,255,0.15);
  cursor: not-allowed;
  box-shadow: none;
}

.bag-use:not(:disabled):hover, .shop-buy:not(:disabled):hover, .open-treasure-btn:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 16px rgba(255,150,0,0.4);
}

.noble-price, .guard-price, .shop-price {
  font-size: 12px;
  color: #ff9600;
  font-weight: 600;
}

.my-noble, .my-guard {
  padding: 12px;
  margin-bottom: 12px;
  background: linear-gradient(135deg, rgba(139,92,246,0.22), rgba(139,92,246,0.06));
  border: 1px solid rgba(139,92,246,0.2);
  border-radius: 12px;
  color: #a78bfa;
  font-size: 13px;
  text-align: center;
  font-weight: 500;
}
.my-guard-scope { font-size: 12px; color: #c4b5fd; }
.guard-owned-tag {
  display: inline-block;
  padding: 1px 6px;
  font-size: 10px;
  color: #10b981;
  background: #d1fae5;
  border-radius: 4px;
  margin-left: 4px;
}

/* 粉丝团区域 */
.fan-club-section {
  margin-top: 16px;
  padding: 14px;
  background: linear-gradient(135deg, #fff6ef, #fff);
  border: 1px solid #ffe4cc;
  border-radius: 12px;
}
.fan-club-title {
  font-size: 14px;
  font-weight: 700;
  color: #ff6a00;
  margin-bottom: 8px;
}
.fan-club-desc {
  font-size: 12px;
  color: #888;
  line-height: 1.6;
  margin-bottom: 12px;
}
.fan-club-btn {
  width: 100%;
  padding: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.25s;
}
.fan-club-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255, 106, 0, 0.3); }
.fan-club-btn:disabled { opacity: 0.6; cursor: default; transform: none; box-shadow: none; }

.noble-item.active, .guard-item.active {
  border: 1px solid #a78bfa;
  background: linear-gradient(135deg, rgba(139,92,246,0.18), rgba(139,92,246,0.05));
  box-shadow: 0 4px 14px rgba(139, 92, 246, 0.15);
}

.treasure-status {
  padding: 12px;
  margin-bottom: 12px;
  background: rgba(255,150,0,0.1);
  border: 1px solid rgba(255,150,0,0.15);
  border-radius: 12px;
}

.treasure-title {
  font-size: 14px;
  color: #ff9600;
  font-weight: 600;
}

.treasure-progress {
  font-size: 12px;
  color: rgba(255,255,255,0.7);
  margin-top: 4px;
}

.open-treasure-btn {
  width: 100%;
  margin-top: 12px;
  padding: 12px;
  font-size: 14px;
  font-weight: 700;
  border-radius: 14px;
}

.shop-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.shop-tabs span {
  flex: 1;
  text-align: center;
  padding: 10px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 10px;
  color: rgba(255,255,255,0.65);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.shop-tabs span.active, .shop-tabs span:hover {
  background: linear-gradient(135deg, rgba(255,150,0,0.2), rgba(255,150,0,0.06));
  border-color: rgba(255,150,0,0.35);
  color: #ff9600;
  font-weight: 600;
}

/* ==================== 响应式 ==================== */
/* PC live-room hierarchy: room identity, stage, engagement rail, then chat. */
.live-detail {
  padding: 24px 0 32px;
  background: #eef0f3;
}

.container {
  max-width: 1440px;
  padding: 0 24px;
}

.live-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 356px;
  align-items: stretch;
  gap: 14px;
}

.live-main {
  display: flex;
  flex-direction: column;
  border: 1px solid #dfe3e8;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 12px 36px rgba(37, 43, 54, 0.08);
}

.video-info {
  order: -1;
  padding: 16px 18px 14px;
  gap: 12px;
  border-bottom: 1px solid #e9ecf0;
  background: #fff;
}

.video-title-row { align-items: flex-start; }

.video-title {
  color: #17191d;
  font-size: 21px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.video-title-actions { gap: 8px; }

.video-view-count {
  min-width: 48px;
  justify-content: center;
  padding: 6px 11px;
  border: 1px solid #f0d3b5;
  border-radius: 8px;
  background: #fff8f1;
  color: #c95f00;
  font-variant-numeric: tabular-nums;
}

.follow-btn {
  min-width: 76px;
  padding: 6px 16px;
  border-radius: 8px;
  border-color: #e9700b;
  background: #e9700b;
  color: #fff;
  box-shadow: none;
}

.follow-btn:hover {
  background: #cc5f00;
  color: #fff;
  box-shadow: none;
}

.follow-btn.followed {
  border-color: #d9dde3;
  background: #f3f4f6;
  color: #626a76;
}

.author-info {
  flex-wrap: nowrap;
  gap: 10px;
}

.author-avatar {
  width: 42px;
  height: 42px;
  border: 1px solid #dfe3e8;
}

.author-name {
  color: #25282e;
  font-size: 14px;
}

.author-meta {
  flex: 0 1 auto;
  justify-content: flex-end;
  gap: 6px;
}

.meta-item,
.meta-item.category,
.meta-item.rank-tag,
.meta-item.guard-tag {
  padding: 4px 9px;
  border-color: #e2e5e9;
  border-radius: 7px;
  background: #f6f7f8;
  color: #646b75;
}

.meta-item.category {
  color: #b65700;
  border-color: #f0d3b5;
  background: #fff8f1;
}

.report-link {
  padding: 5px 10px;
  border-radius: 7px;
}

.video-container,
.live-video,
.live-video-fallback { background: #0c0d0f; }

.live-topbar {
  height: 54px;
  padding: 0 16px;
  gap: 10px;
  background: linear-gradient(180deg, rgba(8, 9, 11, 0.78), rgba(8, 9, 11, 0));
}

.live-badge {
  padding: 4px 9px;
  border-radius: 6px;
  background: #e63752;
}

.live-stat {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.video-controls {
  height: 52px;
  padding: 0 14px;
  background: linear-gradient(0deg, rgba(8, 9, 11, 0.9), rgba(8, 9, 11, 0));
}

.control-btn { border-radius: 7px; }

.live-toolbar {
  display: grid;
  grid-template-columns: auto minmax(210px, 1fr) auto;
  grid-template-areas:
    "tools gifts actions";
  gap: 10px 12px;
  min-height: 0;
  padding: 10px 12px 11px;
  border-top: 1px solid #2a2d33;
  background: #17191e;
}

.toolbar-left { grid-area: tools; }
.toolbar-center { grid-area: gifts; min-width: 0; }
.toolbar-right { grid-area: actions; }

.toolbar-group { gap: 6px; }

.toolbar-btn {
  min-height: 34px;
  border-radius: 8px;
  background: #24272e;
  color: #d9dde4;
}

.toolbar-btn:hover {
  border-color: #a94e00;
  background: #302820;
  color: #f3a052;
}

.toolbar-btn.tool-icon {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
}

.toolbar-left .toolbar-btn.tool-icon span { display: none; }

.toolbar-btn.action-btn.recharge { background: #e9700b; }

.toolbar-btn.action-btn.recharge:hover {
  background: #c95f00;
  box-shadow: none;
}

.toolbar-btn.action-btn.noble,
.toolbar-btn.action-btn.noble:hover {
  background: #24272e;
  color: #d9dde4;
  box-shadow: none;
}

.quick-gifts {
  width: 100%;
  max-width: none;
  justify-content: center;
  gap: 7px;
  margin-left: 0;
}

.quick-gift {
  width: 38px;
  height: 38px;
  padding: 4px;
  border-radius: 8px;
  background: #22252b;
}

.quick-gift:hover {
  transform: translateY(-1px);
  border-color: #b95a07;
  background: #302820;
}

.dm-color-picker { flex-shrink: 0; }

.dm-color {
  width: 18px;
  height: 18px;
  border-width: 1px;
}

.dm-input {
  min-width: 180px;
  width: auto;
  height: 38px;
  border-radius: 8px;
  background: #22252b;
}

.dm-chip.small,
.dm-send-btn,
.dm-like-btn { border-radius: 8px; }

.dm-send-btn {
  height: 38px;
  background: #e9700b;
  box-shadow: none;
}

.dm-send-btn:not(:disabled):hover {
  background: #c95f00;
  box-shadow: none;
}

.chat-area {
  width: auto;
  height: clamp(690px, 56.25vw, 825px);
  min-height: 0;
  border: 1px solid #dfe3e8;
  border-radius: 14px;
  box-shadow: 0 12px 36px rgba(37, 43, 54, 0.08);
}

.chat-tabs {
  min-height: 50px;
  padding: 0 14px;
  border-bottom-color: #e6e9ed;
  background: #fff;
}

.chat-tabs.audience-tabs {
  gap: 2px;
  padding: 0 12px;
}

.audience-tabs .chat-tab {
  padding: 16px 8px 14px;
  color: #747b85;
}

.chat-tab.active { color: #b65700; }

.chat-tab.active::after {
  height: 2px;
  background: #e9700b;
}

.chat-online {
  border-radius: 6px;
  background: #fff1f3;
}

.chat-sub-tabs {
  gap: 16px;
  min-height: 42px;
  padding: 0 16px;
  border-bottom-color: #e9ecf0;
  background: #f7f8fa;
}

.chat-sub-tab,
.chat-sub-tab.active {
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #7a818c;
  line-height: 42px;
}

.chat-sub-tab.active {
  color: #b65700;
  font-weight: 700;
}

.chat-messages {
  padding: 10px 16px;
  background: #fff;
}

.live-stream {
  gap: 0;
  background: #fff;
}

.live-msg,
.live-msg-enter,
.live-msg-gift,
.live-msg-like {
  margin: 0;
  padding: 8px 0;
  border: 0;
  border-bottom: 1px solid #f0f2f4;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.msg-nick { color: #34669b; }

.gift-nick,
.gift-name-text,
.gift-count-text,
.like-nick,
.like-text { color: #b65700; }

.system-message {
  margin: 4px 0 10px;
  padding: 10px;
  border-radius: 8px;
  background: #f5f7f9;
  color: #68717d;
}

.live-input-area {
  padding: 12px 14px;
  border-top-color: #e6e9ed;
  background: #fff;
}

.live-input {
  height: 40px;
  border-radius: 8px;
  background: #f6f7f9;
}

.live-send-btn {
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  background: #e9700b;
  box-shadow: none;
}

.live-send-btn:not(:disabled):hover {
  background: #c95f00;
  box-shadow: none;
}

.toolbar-panel {
  border-color: #30343c;
  border-radius: 12px;
  background: #1c1f25;
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.38);
}

@media (max-width: 1100px) {
  .live-content { flex-direction: column; }
  .chat-area { width: 100%; height: 480px; }
  .live-toolbar { gap: 5px; padding: 8px; }
  .toolbar-left .toolbar-btn.tool-icon span { display: none; }
  .toolbar-left .toolbar-btn.tool-icon { padding: 5px 6px; }
  .quick-gifts { max-width: 180px; }
  .interact-panel {
    width: 240px;
    bottom: 100px;
    max-height: 320px;
  }
  .toolbar-panel { bottom: 64px; }
}

@media (max-width: 768px) {
  .live-toolbar {
    justify-content: center;
    min-height: auto;
    padding: 6px;
  }
  .toolbar-group { gap: 4px; }
  .toolbar-btn { padding: 5px 8px; font-size: 11px; }
  .toolbar-btn.tool-icon { padding: 4px 6px; }
  .toolbar-btn.tool-icon span { display: none; }
  .quick-gifts { max-width: 160px; }
  .controls-right { gap: 4px; }
  .volume-slider { display: none; }
  .video-controls { padding: 0 8px; }
  .quick-gift { width: 30px; height: 30px; }
  .dm-input { flex: 1; min-width: 0; }
  .dm-color-picker { display: none; }
  .dm-position-picker { gap: 3px; }
  .dm-position-picker .dm-chip.small { padding: 2px 6px; font-size: 11px; }
  .more-menu { grid-template-columns: repeat(2, 1fr); }
  .gift-picker { grid-template-columns: repeat(3, 1fr); }
  .recharge-list { grid-template-columns: repeat(2, 1fr); }
  .interact-panel {
    width: calc(100% - 24px);
    right: 12px;
    left: 12px;
    bottom: 110px;
    max-height: 50vh;
  }
}

@media (max-width: 640px) {
  .container { padding: 0 10px; }
  .live-content { gap: 12px; }
  .live-topbar { padding: 0 10px; gap: 8px; }
  .toolbar-panel { left: 6px; right: 6px; max-width: calc(100% - 12px); }
  .gift-picker { grid-template-columns: repeat(2, 1fr); }
  .recharge-list { grid-template-columns: repeat(2, 1fr); }
}

/* ===== 虎牙风格开通弹窗 ===== */
.lhy-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
}
.recharge-qr-modal {
  width: min(360px, calc(100vw - 40px));
  overflow: hidden;
  border: 1px solid #3a3f4b;
  border-radius: 14px;
  background: #1b1e25;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}
.recharge-qr-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #2c303a;
  background: #20242c;
}
.recharge-qr-title {
  color: #f5c876;
  font-size: 16px;
  font-weight: 700;
}
.recharge-qr-close {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 50%;
  background: #2e333d;
  color: #b9bec9;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s;
}
.recharge-qr-body {
  padding: 20px 24px;
  text-align: center;
}
.recharge-qr-amount {
  font-size: 28px;
  font-weight: 700;
  color: #f5c876;
}
.recharge-qr-desc {
  margin-top: 6px;
  color: #b9bec9;
  font-size: 14px;
}
.recharge-qr-wrap {
  width: 220px;
  height: 220px;
  margin: 18px auto;
  padding: 10px;
  border-radius: 10px;
  background: #fff;
}
.recharge-qr-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.recharge-qr-tip {
  color: #8b93a3;
  font-size: 12px;
  margin: 0;
}
.lhy-modal {
  width: min(420px, calc(100vw - 32px));
  max-height: min(80vh, 640px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #3a3f4b;
  border-radius: 14px;
  background: #1b1e25;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}
.lhy-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #2c303a;
  background: #20242c;
  flex-shrink: 0;
}
.lhy-modal-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #f5c876;
  font-size: 16px;
  font-weight: 700;
}
.lhy-modal-close {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 50%;
  background: #2e333d;
  color: #b9bec9;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s;
}
.lhy-modal-close:hover {
  background: #3d434f;
  color: #fff;
}
.lhy-modal-body {
  padding: 16px 18px 20px;
  overflow-y: auto;
}
.lhy-current {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 10px 14px;
  border: 1px solid rgba(233, 112, 11, 0.35);
  border-radius: 10px;
  background: rgba(233, 112, 11, 0.08);
  font-size: 13px;
}
.lhy-current.none {
  border-color: #343945;
  background: #20242c;
  color: #8d93a0;
}
.lhy-current-label { color: #9aa1ae; }
.lhy-current-name { color: #f3a052; font-weight: 700; }
.lhy-current-expire { color: #9aa1ae; }
.lhy-level-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.lhy-level-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #2e333d;
  border-radius: 10px;
  background: #22262e;
  cursor: pointer;
  transition: all 0.2s;
}
.lhy-level-item:hover { border-color: #a94e00; background: #2a261f; }
.lhy-level-item.active { border-color: #e9700b; background: rgba(233, 112, 11, 0.08); }
.lhy-level-item img {
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: 8px;
  object-fit: cover;
  background: #2c313b;
}
.lhy-level-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.lhy-level-name { color: #eef1f6; font-size: 15px; font-weight: 700; }
.lhy-level-benefit {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #8f96a3;
  font-size: 12px;
}
.lhy-level-buy {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
}
.lhy-level-price { color: #f3a052; font-size: 13px; font-weight: 700; white-space: nowrap; }
.lhy-buy-btn {
  padding: 6px 16px;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, #ff7a18, #ff9b37);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}
.lhy-buy-btn:hover { filter: brightness(1.1); }
.lhy-buy-btn.owned {
  background: #2e333d;
  color: #9aa1ae;
  cursor: default;
}
/* 守护弹窗：主播信息 */
.lhy-guard-anchor {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #2e333d;
  border-radius: 10px;
  background: #20242c;
}
.lhy-guard-anchor img {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  object-fit: cover;
  background: #2c313b;
}
.lhy-guard-anchor > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.lhy-guard-anchor-name { color: #eef1f6; font-size: 15px; font-weight: 700; }
.lhy-guard-anchor-tip { color: #8f96a3; font-size: 12px; }
/* 守护弹窗：粉丝团 */
.lhy-fanclub {
  margin-top: 16px;
  padding: 14px;
  border: 1px dashed #3a3f4b;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
}
.lhy-fanclub-title { color: #f3a052; font-size: 14px; font-weight: 700; margin-bottom: 6px; }
.lhy-fanclub-desc { margin: 0 0 10px; color: #8f96a3; font-size: 12px; line-height: 1.6; }
.lhy-fanclub-btn {
  width: 100%;
  padding: 9px 0;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #ff7a18, #ff9b37);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.lhy-fanclub-btn:hover { filter: brightness(1.08); }
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.22s ease;
}
.modal-fade-enter-active .lhy-modal,
.modal-fade-leave-active .lhy-modal {
  transition: transform 0.22s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-from .lhy-modal,
.modal-fade-leave-to .lhy-modal {
  transform: translateY(16px) scale(0.96);
}
@media (max-width: 640px) {
  .lhy-modal-body { padding: 14px; }
  .lhy-level-item { padding: 10px 12px; }
  .lhy-level-item img { width: 40px; height: 40px; }
}
</style>
